import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CONFIG from "../../config";
import { useCart } from "./CartContext";
import GoogleMapPreview from "../common/GoogleMapPreview";
import CurrentLocationDetector from "../common/CurrentLocationDetector";

// ─────────────────────────────────────────────────────────────────────────────
// AUTH HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getSafeLoggedInUser = () => {
  try {
    const raw   = localStorage.getItem("currentUser");
    const token = localStorage.getItem("authToken") || "";
    if (!raw && !token) return null;
    return { ...JSON.parse(raw || "{}"), token };
  } catch { return null; }
};

const getAuthMobile = () => {
  const u = getSafeLoggedInUser();
  return u?.mobile || u?.phone || u?.mobile_number || "";
};

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token")        || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const CACHE_TTL       = 24 * 60 * 60 * 1000;
const ORDER_CACHE_TTL =       60 * 1000;     // order status can change any minute
const CART_KEY    = "cartItems";
const BASE_URL    = CONFIG?.API_BASE_URL || "http://localhost:8000";
const GMAPS_KEY   = CONFIG?.GOOGLE_MAPS_API_KEY || "";

const buildStaticMapUrl = (addr) => {
  const parts = [addr.line1, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean);
  if (!parts.length || !GMAPS_KEY) return "";
  const encoded = encodeURIComponent(parts.join(", "));
  return `https://maps.googleapis.com/maps/api/staticmap?center=${encoded}&zoom=16&size=600x200&scale=2&markers=color:red%7C${encoded}&key=${GMAPS_KEY}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// CACHE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getCache = (key) => {
  try {
    const p = JSON.parse(localStorage.getItem(key) || "null");
    if (!p?.expiry || Date.now() > p.expiry) { localStorage.removeItem(key); return null; }
    return p.value;
  } catch { return null; }
};

const setCache = (k, v, ttl = CACHE_TTL) =>
  localStorage.setItem(k, JSON.stringify({ value: v, expiry: Date.now() + ttl }));

// ─────────────────────────────────────────────────────────────────────────────
// CART HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const readLocalCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); }
  catch { return []; }
};

const writeLocalCart = (items) => {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event("cart-updated"));
};

const clearLocalCart = () => {
  localStorage.removeItem(CART_KEY);
  window.dispatchEvent(new Event("cart-updated"));
};

// ── Normalise a raw cart item so SummaryPage always gets consistent fields ──
const normalizeCartItem = (item) => ({
  id:            item.id       || item.service_id || "",
  dbId:          item.dbId     || String(item._id || item.id || ""),
  title:         item.title    || item.service_name || item.name || "Service",
  name:          item.name     || item.title        || item.service_name || "Service",
  short:         item.short    || item.subtitle     || item.category || "",
  subtitle:      item.subtitle || item.short        || item.category || "",
  price:         Number(item.price || item.amount || 0),
  image:         item.image    || null,
  category:      item.category || "",
  qty:           Number(item.qty || 1),
  bookingDate:   item.bookingDate   || "",
  bookingTime:   item.bookingTime   || "",
  bookingSlotId: item.bookingSlotId || "",
});

const normalizeCart = (items = []) => {
  const arr = Array.isArray(items) ? items : [];
  const map = new Map();
  arr.forEach((item) => {
    const n = normalizeCartItem(item);
    if (!n.id) return;
    if (map.has(n.id)) {
      const ex = map.get(n.id);
      map.set(n.id, { ...ex, qty: ex.qty + n.qty });
    } else {
      map.set(n.id, n);
    }
  });
  return Array.from(map.values());
};

// ─────────────────────────────────────────────────────────────────────────────
// API URLS
// ─────────────────────────────────────────────────────────────────────────────
const API = {
  userProfile:           (id)  => `${BASE_URL}/api/user/user-profile/${encodeURIComponent(id)}/`,
  scheduledJobs:         (m)   => `${BASE_URL}/api/user/user-scheduled-jobs/${encodeURIComponent(m)}/`,
  babyCaretakerJobs:     (m)   => `${BASE_URL}/api/user/baby-caretaker-bookings/${encodeURIComponent(m)}/`,
  cancelBabyCaretaker:   (id)  => `${BASE_URL}/api/requirements/baby-caretaker/${encodeURIComponent(id)}/cancel/`,
  babyCaretakerSlots:    (id)  => `${BASE_URL}/api/user/baby-caretaker-slots/${encodeURIComponent(id)}/`,
  raiseComplaint:        ()    => `${BASE_URL}/api/user/raise-complaint/`,
  userAddresses:         (uid) => `${BASE_URL}/api/user/user-addresses/${encodeURIComponent(uid)}/`,
  logout:                ()    => `${BASE_URL}/api/user/user-logout/`,
  cartGet:       (m)    => `${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/`,
  cartAdd:       (m)    => `${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/add/`,
  cartRemove:    (m,id) => `${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/remove/${encodeURIComponent(id)}/`,
  cartRemoveBySid:(m,sid)=>`${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/remove-by-service/${encodeURIComponent(sid)}/`,
  cartUpdateQty: (m,id) => `${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/update-qty/${encodeURIComponent(id)}/`,
  cartClear:     (m)    => `${BASE_URL}/api/user/user-cart/${encodeURIComponent(m)}/clear/`,
  // order config + user orders + cancel
  orderConfig:            ()    => `${BASE_URL}/api/order/config/`,
  updateOrderConfig:      ()    => `${BASE_URL}/api/order/config/update/`,
  userScheduledOrders:    (m)   => `${BASE_URL}/api/orders/user/scheduled/${encodeURIComponent(m)}/`,
  userRequirementOrders:  (m)   => `${BASE_URL}/api/orders/user/requirement/${encodeURIComponent(m)}/`,
  // Unified "Booked Services" feed — category/booking_type/status filters,
  // server-side paginated, merged across every booking collection. See
  // get_user_bookings in the backend for the full source list.
  userAllBookings: (m, { category = "all", bookingType = "all", status = "all", page = 1, limit = 10 } = {}) =>
    `${BASE_URL}/api/orders/user/bookings/${encodeURIComponent(m)}/`
    + `?category=${encodeURIComponent(category)}&booking_type=${encodeURIComponent(bookingType)}`
    + `&status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`,
  // Special-service wizards (Rapid Cleaning, Deep Cleaning, etc.) each write
  // to their own dedicated collection — NOT scheduled_order_col — so they
  // never showed up in userScheduledOrders above. Rapid Cleaning wired in
  // here as read-only entries (see loadUserOrders); the other 5 special
  // services have the same gap but aren't wired in yet.
  rapidCleaningBookings:  (m)   => `${BASE_URL}/api/special-service/rapid-cleaning/user/${encodeURIComponent(m)}/`,
  cancelScheduledOrder:   (id)  => `${BASE_URL}/api/orders/scheduled/${encodeURIComponent(id)}/cancel/`,
  cancelRequirementOrder: (id)  => `${BASE_URL}/api/orders/requirement/${encodeURIComponent(id)}/cancel/`,
  payScheduledOrder:      (id)  => `${BASE_URL}/api/orders/scheduled/${encodeURIComponent(id)}/pay/`,
  confirmJob:             (id)  => `${BASE_URL}/api/orders/${encodeURIComponent(id)}/user-confirm/`,
  membershipOutline:      (m)   => `${BASE_URL}/api/user/membership/outline/${encodeURIComponent(m)}/`,
  membershipSchedule:     (id)  => `${BASE_URL}/api/user/membership/${encodeURIComponent(id)}/schedule/`,
  membershipCancel:       (id)  => `${BASE_URL}/api/user/membership/${encodeURIComponent(id)}/cancel/`,
  membershipSlotAvailability: () => `${BASE_URL}/api/membership/slot-availability/`,
  festivalCleaningStatus: (id)  => `${BASE_URL}/api/user/membership/${encodeURIComponent(id)}/festival-cleaning/status/`,
  festivalCleaningRedeem: (id)  => `${BASE_URL}/api/user/membership/${encodeURIComponent(id)}/festival-cleaning/redeem/`,
  couponStatus:           ()    => `${BASE_URL}/api/user/coupon/status/`,
};

// ─────────────────────────────────────────────────────────────────────────────
// CANCELLATION HELPERS (pure — no side-effects)
// ─────────────────────────────────────────────────────────────────────────────

const calcCancellation = (order, config, orderType = "scheduled") => {
  const fallbackCharges = CONFIG?.CANCELLATION_CHARGES
    ? [
        { condition: "before_accept", label: CONFIG.CANCELLATION_CHARGES.before_accept.label, max_status: 0, percent: CONFIG.CANCELLATION_CHARGES.before_accept.percent },
        { condition: "after_accept",  label: CONFIG.CANCELLATION_CHARGES.after_accept.label,  min_status: 1, max_status: 1, percent: CONFIG.CANCELLATION_CHARGES.after_accept.percent },
        { condition: "after_start",   label: CONFIG.CANCELLATION_CHARGES.after_start.label,   min_status: 2, percent: CONFIG.CANCELLATION_CHARGES.after_start.percent },
      ]
    : [];
  const charges = (config?.charges?.length ? config.charges : fallbackCharges);
  const offers  = config?.offers || [];

  // Normalise order status to a number
  const _strMap = { new: 0, accepted: 1, started: 2, in_progress: 2, completed: 3, declined: 4 };
  const rawStatus = order.status ?? 0;
  const status = typeof rawStatus === "number"
    ? rawStatus
    : (_strMap[String(rawStatus).toLowerCase()] ?? 0);

  const basePrice = parseFloat(order.service_charge || order.total_amount || order.amount || order.price || 0);

  // Pick charge tier by order status (descending so most-specific matches first)
  let chargePercent  = 15;
  let conditionLabel = "After work has started";
  const sorted = [...charges].sort((a, b) => (b.min_status ?? 0) - (a.min_status ?? 0));
  for (const tier of sorted) {
    const minS = tier.min_status ?? null;
    const maxS = tier.max_status ?? null;
    if (minS != null && maxS == null && status >= minS) {
      chargePercent = tier.percent ?? 15; conditionLabel = tier.label ?? ""; break;
    }
    if (minS != null && maxS != null && status >= minS && status <= maxS) {
      chargePercent = tier.percent ?? 10; conditionLabel = tier.label ?? ""; break;
    }
    if (minS == null && maxS != null && status <= maxS) {
      chargePercent = tier.percent ?? 5;  conditionLabel = tier.label ?? ""; break;
    }
  }

  const charge = Math.round(basePrice * chargePercent / 100);

  // Pick best offer
  const bestOffer     = [...offers].sort((a, b) => b.min_base_price - a.min_base_price).find(o => basePrice >= o.min_base_price);
  const offerDiscount = bestOffer?.discount ?? 0;

  return {
    charge,
    chargePercent,
    conditionLabel,
    offerDiscount,
    netCharge:    Math.max(0, charge - offerDiscount),
    basePrice,
    appliedOffer: bestOffer?.label ?? null,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// apiFetch — 404 returns null (endpoint not yet wired), other errors throw
// ─────────────────────────────────────────────────────────────────────────────
const apiFetch = async (url, opts = {}) => {
  try {
    const res  = await fetch(url, { credentials:"include", headers:authHeaders(), ...opts });
    const text = await res.text();
    if (res.status === 404) { console.warn(`[API] 404: ${url}`); return null; }
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    return res.status === 204 ? null : JSON.parse(text);
  } catch (e) { console.error("[API] error:", e.message); throw e; }
};

// ─────────────────────────────────────────────────────────────────────────────
// CART DB HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const deleteFromMongo = async (mobile, item) => {
  if (!mobile || !item) return;
  try {
    if (item.dbId) await apiFetch(API.cartRemove(mobile, item.dbId), { method:"DELETE" });
    else {
      const sid = String(item.id || item.service_id || "");
      if (sid) await apiFetch(API.cartRemoveBySid(mobile, sid), { method:"DELETE" });
    }
  } catch (e) { console.warn("[Cart] delete failed:", e.message); }
};

const updateQtyInMongo = async (mobile, item, newQty) => {
  if (!mobile || !item) return;
  try {
    await apiFetch(API.cartUpdateQty(mobile, item.dbId || String(item.id)), {
      method:"PATCH", body:JSON.stringify({ qty:newQty }),
    });
  } catch (e) { console.warn("[Cart] qty update failed:", e.message); }
};

const listFrom = (data) => {
  if (!data) return [];
  if (Array.isArray(data))          return data;
  if (Array.isArray(data?.data))    return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.bookings))return data.bookings;
  if (Array.isArray(data?.jobs))    return data.jobs;
  if (Array.isArray(data?.items))   return data.items;
  return [];
};

// ─────────────────────────────────────────────────────────────────────────────
// MISC
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const pct = (u) => {
  let s = 0;
  if (u?.profile_image)                    s += 20;
  if (u?.mobile || u?.phone)               s += 20;
  if (u?.email)                            s += 20;
  if (u?.default_address || u?.address)    s += 20;
  if (u?.payment_added)                    s += 20;
  return s;
};

const purgeCache = () => {
  ["authToken","accessToken","access_token","accesstoken","token","currentUser","tempCart"]
    .forEach((k) => localStorage.removeItem(k));
  clearLocalCart();
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && (k.startsWith("profile_") || k.startsWith("overview_") ||
              k.startsWith("bookings_") || k.startsWith("requirements_") ||
              k.startsWith("addresses_") || k.startsWith("backendCart_")))
      keys.push(k);
  }
  keys.forEach((k) => localStorage.removeItem(k));
};

const FALLBACK_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
const FALLBACK_IMG    = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";
const MENU = { main: ["My Profile", "Booked Services", "My Membership", "Cart", "My Requests", "My Addresses"] };

// ─────────────────────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────────────────────
const Toast = ({ msg, onDone }) => {
  useEffect(() => { const t = setTimeout(onDone, 2200); return () => clearTimeout(t); }, [onDone]);
  return <div style={S.toast}>{msg}</div>;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ProfileDashboard = ({ currentUser: propUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart: ctxClearCart } = useCart();

  const initialUser  = useMemo(() => propUser || getSafeLoggedInUser(), [propUser]);
  const eagerMobile  = useMemo(
    () => initialUser?.mobile || initialUser?.phone || initialUser?.mobile_number || getAuthMobile() || "",
    [initialUser]
  );
  const seedId    = initialUser?.id || initialUser?.user_id || "";
  const isLoggedIn = !!(initialUser || eagerMobile);

  const [profileUser,    setProfileUser]    = useState(initialUser || null);
  const [localCart,      setLocalCartRaw]   = useState(() => normalizeCart(readLocalCart()));
  const [activeMenu,     setActiveMenu]     = useState("My Profile");
  const [pageLoading,    setPageLoading]    = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [loggingOut,     setLoggingOut]     = useState(false);
  const [error,          setError]          = useState("");
  const [toast,          setToast]          = useState("");
  const [overviewData,    setOverviewData]    = useState({ scheduledJobs: [] });
  const [,               setBookingData]      = useState({ scheduledJobs: [] });
  const [requirementData,      setRequirementData]      = useState([]);
  const [addresses,            setAddresses]            = useState([]);
  const [addrSaving,           setAddrSaving]           = useState(false);
  const [,                     setDbCartData]            = useState([]);
  const [userScheduledOrders,  setUserScheduledOrders]  = useState([]);
  const [userRequirementOrders,setUserRequirementOrders]= useState([]);
  const [userOrdersLoading,    setUserOrdersLoading]    = useState(false);
  const [cancelConfig,         setCancelConfig]         = useState(null);
  const [cancelModal,          setCancelModal]          = useState(null);
  // handlePayOrder navigates straight away (no async gap on this page to
  // show a spinner for), so this never needs to be set — only read by
  // OrderCard/PaymentDueModal, always null.
  const [payingOrderId]                                 = useState(null);
  const [confirmingJobId,      setConfirmingJobId]      = useState(null);
  const [payModal,             setPayModal]             = useState(null);
  const [paymentDueModal,      setPaymentDueModal]      = useState(null);
  // Booked Services sub-tabs (All Bookings / Scheduled / Home / Technical /
  // Special Service / Caretaker) all render the same unified feed below,
  // each just presetting bookingFilters differently (see loadAllBookings).
  const [bookedTab,            setBookedTab]            = useState("all");
  const [allBookings,          setAllBookings]          = useState([]);
  const [allBookingsLoading,   setAllBookingsLoading]   = useState(false);
  const [allBookingsPage,      setAllBookingsPage]      = useState({ page: 1, totalPages: 0, total: 0 });
  const [bookingFilters,       setBookingFilters]       = useState({ category: "all", bookingType: "all", status: "all" });
  const [memberships,          setMemberships]          = useState([]);
  const [couponStatus,         setCouponStatus]         = useState(null); // { coupon_valid, successful_referral_count, referral_coupon_total/used/remaining, available_coupons[] }
  const [couponLoading,        setCouponLoading]        = useState(false);
  const [detailModal,          setDetailModal]          = useState(null); // membership whose day-by-day schedule popup is open
  const [membershipCancelModal,setMembershipCancelModal]= useState(null); // membership pending cancel confirmation
  const hasMergedRef = useRef(false);

  const currentMobile  = profileUser?.mobile || profileUser?.phone || profileUser?.mobile_number || eagerMobile;
  const currentUserId  = profileUser?.id || profileUser?.user_id || seedId;
  const userName       = profileUser?.name || profileUser?.full_name || initialUser?.name || "User";
  const userEmail      = profileUser?.email || initialUser?.email || "—";
  const userAvatar     = profileUser?.profile_image || FALLBACK_AVATAR;
  const memberSince    = profileUser?.member_since || profileUser?.date_joined?.slice(0, 7) || "—";

  // ── Local cart writer — normalises every write ───────────────────────────
  const setLocalCart = useCallback((updater) => {
    setLocalCartRaw((prev) => {
      const raw  = typeof updater === "function" ? updater(prev) : updater;
      const next = normalizeCart(raw);
      writeLocalCart(next);
      return next;
    });
  }, []);

  const cartCount = localCart.reduce((s, i) => s + Number(i.qty || 1), 0);
  const cartTotal = localCart.reduce((s, i) => s + Number(i.price || 0) * Number(i.qty || 1), 0);

  // ── Cart handlers ────────────────────────────────────────────────────────
  const handleQtyChange = useCallback((itemId, newQty) => {
    const mobile = currentMobile || eagerMobile;
    setLocalCart((prev) => {
      const item = prev.find((i) => String(i.id) === String(itemId));
      if (newQty <= 0) {
        if (item && isLoggedIn && mobile) deleteFromMongo(mobile, item);
        return prev.filter((i) => String(i.id) !== String(itemId));
      }
      if (item && isLoggedIn && mobile) updateQtyInMongo(mobile, item, newQty);
      return prev.map((i) => String(i.id) === String(itemId) ? { ...i, qty: newQty } : i);
    });
  }, [isLoggedIn, currentMobile, eagerMobile, setLocalCart]);

  const handleRemoveFromCart = useCallback((itemId) => {
    const mobile = currentMobile || eagerMobile;
    setLocalCart((prev) => {
      const item = prev.find((i) => String(i.id) === String(itemId));
      if (item && isLoggedIn && mobile) deleteFromMongo(mobile, item);
      return prev.filter((i) => String(i.id) !== String(itemId));
    });
  }, [isLoggedIn, currentMobile, eagerMobile, setLocalCart]);

  const handleClearCart = useCallback(() => {
    setLocalCart([]);
    const mobile = currentMobile || eagerMobile;
    if (isLoggedIn && mobile)
      apiFetch(API.cartClear(mobile), { method:"DELETE" })
        .catch((e) => console.warn("[Cart] clear failed:", e.message));
  }, [isLoggedIn, currentMobile, eagerMobile, setLocalCart]);

  // ── Data loaders ─────────────────────────────────────────────────────────
  const loadProfile = useCallback(async (force = false) => {
    const lid = seedId || eagerMobile;
    if (!lid) return;
    const ck = `profile_${lid}`;
    try {
      setError(""); setProfileLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setProfileUser(c); return; } }
      const data = await apiFetch(API.userProfile(lid));
      const user = data?.user || data?.data || data?.profile || data;
      if (user && typeof user === "object") {
        setProfileUser(user);
        setCache(ck, user);
        localStorage.setItem("currentUser", JSON.stringify(user));
        window.dispatchEvent(new Event("auth-updated"));
      }
    } catch { if (initialUser) setProfileUser(initialUser); setError("Could not refresh profile."); }
    finally { setProfileLoading(false); }
  }, [seedId, eagerMobile, initialUser]);

  const loadOverview = useCallback(async (force = false) => {
    const m = currentMobile;
    if (!m) return;
    const ck = `overview_${m}`;
    try {
      setError("");
      if (!force) { const c = getCache(ck); if (c) { setOverviewData(c); return; } }
      const scD = await apiFetch(API.scheduledJobs(m));
      const d   = { scheduledJobs: listFrom(scD) };
      setOverviewData(d); setCache(ck, d);
    } catch (e) { setError(e.message || "Failed to load overview."); }
  }, [currentMobile]);

  const loadBookings = useCallback(async (force = false) => {
    if (!currentMobile) return;
    const ck  = `bookings_${currentMobile}`;
    const ckR = `requirements_${currentMobile}`;
    try {
      setError(""); setSectionLoading(true);
      if (!force) {
        const c  = getCache(ck);
        const cr = getCache(ckR);
        if (c)  setBookingData(c);
        if (cr) setRequirementData(cr);
        if (c && cr) return;
      }
      const [data, reqData] = await Promise.all([
        apiFetch(API.scheduledJobs(currentMobile)),
        apiFetch(API.babyCaretakerJobs(currentMobile)),
      ]);
      const d    = { scheduledJobs: listFrom(data) };
      const reqs = listFrom(reqData);
      setBookingData(d);        setCache(ck, d);
      setRequirementData(reqs); setCache(ckR, reqs);
    } catch (e) { setError(e.message || "Failed to load bookings."); }
    finally { setSectionLoading(false); }
  }, [currentMobile]);

  // ── Membership (subscription plan) — visit-credit wallet ──────────────────
  // Card list is sourced from the master collections (subscription_weekly_master
  // / subscription_monthly_master) via the outline endpoint — the backend
  // patches in live visits_used/visits_remaining/status/visits[] from the
  // canonical user_membership_col record so redemptions still show up
  // immediately, but the base record itself comes from the master.
  const loadMemberships = useCallback(async (force = false) => {
    if (!currentMobile) return;
    const ck = `memberships_${currentMobile}`;
    try {
      setError(""); setSectionLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setMemberships(c); return; } }
      const data = await apiFetch(API.membershipOutline(currentMobile));
      const list = Array.isArray(data?.data) ? data.data : [];
      setMemberships(list); setCache(ck, list, ORDER_CACHE_TTL);
    } catch (e) { setError(e.message || "Failed to load membership."); }
    finally { setSectionLoading(false); }
  }, [currentMobile]);

  // ── Coupons / referral rewards — shown on the My Profile tab + the
  // sidebar widget. Always recalculated server-side (never trusts a
  // cached "coupon_valid"/discount value for longer than ORDER_CACHE_TTL,
  // same short TTL the live order lists use) since redemption/expiry can
  // change this at any time.
  const loadCouponStatus = useCallback(async (force = false) => {
    const identifier = currentUserId || currentMobile;
    if (!identifier) return;
    const ck = `coupon_status_${identifier}`;
    try {
      setCouponLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setCouponStatus(c); return; } }
      const data = await apiFetch(API.couponStatus(), {
        method: "POST",
        body: JSON.stringify({ user_id: currentUserId, mobile: currentMobile }),
      });
      const status = data?.data || data;
      if (status && typeof status === "object") { setCouponStatus(status); setCache(ck, status, ORDER_CACHE_TTL); }
    } catch (e) { console.warn("[Coupons] load failed:", e.message); }
    finally { setCouponLoading(false); }
  }, [currentUserId, currentMobile]);

  // ── Cancel membership ───────────────────────────────────────────────────
  // Confirm modal -> POST /api/user/membership/<id>/cancel/, which removes
  // every pending/assigned/completed day-of-work record for this membership,
  // soft-cancels the canonical order (status -> "cancelled", kept for
  // payment/redemption history), and stamps cancelled_date on the matching
  // master doc — the same one this list is sourced from, so the card
  // reflects "Cancelled" on refresh.
  const handleCancelMembership = useCallback((membership) => {
    setMembershipCancelModal(membership);
  }, []);

  const confirmCancelMembership = useCallback(async () => {
    if (!membershipCancelModal) return;
    const membershipId = membershipCancelModal.membership_id;
    try {
      await apiFetch(API.membershipCancel(membershipId), {
        method: "POST",
        body:   JSON.stringify({ mobile: currentMobile }),
      });
      setMembershipCancelModal(null);
      setToast("Membership cancelled.");
      localStorage.removeItem(`memberships_${currentMobile}`);
      loadMemberships(true);
    } catch (e) {
      setToast("Cancel failed: " + (e.message || "Unknown error"));
      setMembershipCancelModal(null);
    }
  }, [membershipCancelModal, currentMobile, loadMemberships]);

  const loadRequirements = useCallback(async (force = false) => {
    if (!currentMobile) return;
    const ck = `requirements_${currentMobile}`;
    try {
      setError(""); setSectionLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setRequirementData(c); return; } }
      const data = await apiFetch(API.babyCaretakerJobs(currentMobile));
      const reqs = listFrom(data);
      setRequirementData(reqs); setCache(ck, reqs);
    } catch (e) { setError(e.message || "Failed to load requirements."); }
    finally { setSectionLoading(false); }
  }, [currentMobile]);

  // ── Load scheduled_order + requirement_order collections ──────────────────
  const loadUserOrders = useCallback(async (force = false) => {
    if (!currentMobile) return;
    const ckS = `sched_orders_${currentMobile}`;
    const ckR = `req_orders_${currentMobile}`;
    try {
      setUserOrdersLoading(true);
      if (!force) {
        const cs = getCache(ckS);
        const cr = getCache(ckR);
        if (cs) setUserScheduledOrders(cs);
        if (cr) setUserRequirementOrders(cr);
        if (cs && cr) { setUserOrdersLoading(false); return; }
      }
      const [sData, rData, rapidData] = await Promise.all([
        apiFetch(API.userScheduledOrders(currentMobile)),
        apiFetch(API.userRequirementOrders(currentMobile)),
        apiFetch(API.rapidCleaningBookings(currentMobile)).catch(() => null),
      ]);
      const sOrders = Array.isArray(sData?.orders) ? sData.orders : listFrom(sData);
      const rOrders = Array.isArray(rData?.orders) ? rData.orders : listFrom(rData);
      // Normalise special_service_rapidCleaning's own doc shape onto the
      // flat fields OrderCard reads (item.date/slot/total_amount) — its
      // `status` is already the same 0-9 numeric code every other order
      // uses, so that one field needs no translation. Flagged __readOnly
      // since Cancel/Pay/Confirm here would hit scheduled-order endpoints
      // this doc was never written through.
      const rapidOrders = listFrom(rapidData).map((d) => ({
        ...d,
        date:          d?.schedule?.date || "",
        slot:          d?.schedule?.slot_label || d?.schedule?.time_range || "",
        total_amount:  d?.pricing?.total ?? 0,
        service_category: "special_services",
        __readOnly: true,
      }));
      const mergedScheduled = [...sOrders, ...rapidOrders]
        .sort((a, b) => String(b?.created_at || "").localeCompare(String(a?.created_at || "")));
      setUserScheduledOrders(mergedScheduled);  setCache(ckS, mergedScheduled, ORDER_CACHE_TTL);
      setUserRequirementOrders(rOrders); setCache(ckR, rOrders, ORDER_CACHE_TTL);
    } catch (e) { console.error("[UserOrders]", e.message); }
    finally { setUserOrdersLoading(false); }
  }, [currentMobile]);

  // ── Load the unified "All Bookings" feed (category/type/status filters) ──
  // Server-side filtered and paginated — see get_user_bookings. Not cached
  // locally like loadUserOrders above, since the filter/page combination
  // changes often enough that a cache would mostly just miss.
  const loadAllBookings = useCallback(async (page = 1) => {
    if (!currentMobile) return;
    try {
      setAllBookingsLoading(true);
      const data = await apiFetch(API.userAllBookings(currentMobile, {
        category:    bookingFilters.category,
        bookingType: bookingFilters.bookingType,
        status:      bookingFilters.status,
        page,
        limit: 10,
      }));
      setAllBookings(listFrom(data));
      setAllBookingsPage({
        page:       data?.pagination?.page || 1,
        totalPages: data?.pagination?.total_pages || 0,
        total:      data?.pagination?.total || 0,
      });
    } catch (e) {
      console.error("[AllBookings]", e.message);
      setAllBookings([]);
    } finally { setAllBookingsLoading(false); }
  }, [currentMobile, bookingFilters]);

  // ── Load cancellation config ───────────────────────────────────────────────
  const loadCancelConfig = useCallback(async () => {
    try {
      const data = await apiFetch(API.orderConfig());
      if (data) setCancelConfig(data);
    } catch (e) { console.warn("[CancelConfig]", e.message); }
  }, []);

  // ── Cancel handler ────────────────────────────────────────────────────────
  const handleCancelOrder = useCallback(async (order, orderType) => {
    const info = calcCancellation(order, cancelConfig, orderType);
    setCancelModal({ order, orderType, info });
  }, [cancelConfig]);

  const confirmCancelOrder = useCallback(async () => {
    if (!cancelModal) return;
    const { order, orderType } = cancelModal;
    const orderId = order._id || order.id;
    try {
      const url = orderType === "scheduled"
        ? API.cancelScheduledOrder(orderId)
        : API.cancelRequirementOrder(orderId);
      const res = await apiFetch(url, { method: "POST" });
      setCancelModal(null);

      // Show refund toast when applicable
      const refund = res?.refund;
      if (refund && refund.refund_amount > 0) {
        setToast(`Order cancelled. Refund of ₹${refund.refund_amount} will be processed within 5–7 business days.`);
      } else if (refund && refund.refund_amount === 0) {
        setToast("Order cancelled. No refund — full cancellation charge applied.");
      } else {
        setToast("Order cancelled successfully.");
      }

      // Invalidate cache and refresh — loadAllBookings drives the visible
      // Booked Services list now, so that's what needs to reflect the
      // cancellation; loadUserOrders is kept in sync too since its cache
      // is still read elsewhere (e.g. the reschedule/refresh flow below).
      localStorage.removeItem(`sched_orders_${currentMobile}`);
      localStorage.removeItem(`req_orders_${currentMobile}`);
      loadUserOrders(true);
      loadAllBookings(allBookingsPage.page);
    } catch (e) {
      setToast("Cancel failed: " + (e.message || "Unknown error"));
      setCancelModal(null);
    }
  }, [cancelModal, currentMobile, loadUserOrders, loadAllBookings, allBookingsPage.page]);

  // ── Confirm job done (user approves partner's completed status) ──────────
  const handleConfirmJob = useCallback(async (item) => {
    const docId = item?._id || item?.order_id;
    if (!docId || confirmingJobId) return;

    // Special-service bookings (Rapid Cleaning, etc.) skip the scheduled-
    // order tip/checkout flow below — /checkout-schedule/summary isn't
    // special-service aware. The backend's user-confirm endpoint already
    // works unchanged against these (see _resolve_order_collection in
    // views.py), so confirm them directly and refresh the unified list.
    if (item?.category === "special") {
      setConfirmingJobId(docId);
      try {
        await apiFetch(API.confirmJob(docId), {
          method: "POST",
          body:   JSON.stringify({ mobile: currentMobile }),
        });
        setToast("Job confirmed. Thank you!");
        loadAllBookings(allBookingsPage.page);
      } catch (e) {
        setToast("Confirm failed: " + (e.message || "Unknown error"));
      } finally {
        setConfirmingJobId(null);
      }
      return;
    }

    // pay_now — already paid in full. Let the user optionally add a tip
    // before the job is closed, instead of closing it immediately with
    // no chance to tip.
    if (item?.payment_mode !== "pay_after_work") {
      navigate("/checkout-schedule/summary", { state: { mode: "confirm_tip", existingOrder: item } });
      return;
    }

    setConfirmingJobId(docId);
    try {
      await apiFetch(API.confirmJob(docId), {
        method: "POST",
        body:   JSON.stringify({ mobile: currentMobile }),
      });
      localStorage.removeItem(`sched_orders_${currentMobile}`);
      // Redirect to payment page — pass the latest order data so amounts are fresh
      navigate("/checkout-schedule/summary", {
        state: { mode: "pay_order", existingOrder: item },
      });
    } catch (e) {
      setToast("Confirm failed: " + (e.message || "Unknown error"));
    } finally {
      setConfirmingJobId(null);
    }
  }, [confirmingJobId, currentMobile, navigate, loadAllBookings, allBookingsPage.page]); // eslint-disable-line

  // ── Payment handler — navigates to /checkout-schedule/summary in pay_order mode ────
  const handlePayOrder = useCallback((item) => {
    navigate("/checkout-schedule/summary", {
      state: { mode: "pay_order", existingOrder: item },
    });
  }, [navigate]);

  // ── Reschedule handler — for orders cancelled by partner ──────────────────
  const handleReschedule = useCallback((item) => {
    navigate("/checkout-schedule/summary", {
      state: { mode: "reschedule", existingOrder: item },
    });
  }, [navigate]);

  const loadAddresses = useCallback(async (force = false) => {
    const uid = currentUserId || currentMobile;
    if (!uid) return;
    const ck = `addresses_${uid}`;
    try {
      setSectionLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setAddresses(c); return; } }
      const data = await apiFetch(API.userAddresses(uid));
      const list = listFrom(data);
      setAddresses(list); setCache(ck, list);
    } catch (e) { setError(e.message || "Failed to load addresses."); }
    finally { setSectionLoading(false); }
  }, [currentUserId, currentMobile]);

  const saveAddresses = useCallback(async (newList) => {
    const uid = currentUserId || currentMobile;
    if (!uid) return;
    try {
      setAddrSaving(true);
      const data = await apiFetch(API.userAddresses(uid), {
        method: "PATCH",
        body:   JSON.stringify({ addresses: newList }),
      });
      const saved = listFrom(data);
      setAddresses(saved);
      localStorage.removeItem(`addresses_${uid}`);
      setToast("Addresses saved.");
    } catch (e) { setToast("Save failed: " + e.message); }
    finally { setAddrSaving(false); }
  }, [currentUserId, currentMobile]);

  // ── Load cart from MongoDB and sync to localStorage ──────────────────────
  const loadDbCart = useCallback(async (force = false) => {
    const mobile = currentMobile || eagerMobile;
    if (!mobile) return;
    const ck = `backendCart_${mobile}`;
    try {
      setSectionLoading(true);
      if (!force) { const c = getCache(ck); if (c) { setDbCartData(c); return; } }
      const json  = await apiFetch(API.cartGet(mobile));
      const items = listFrom(json);
      setDbCartData(items);
      setCache(ck, items);
      if (items.length > 0) {
        const normalized = normalizeCart(items.map((i) => ({
          id:       i.service_id || i.id,
          dbId:     String(i._id || i.id),
          title:    i.title || i.service_name || i.name || "Service",
          name:     i.name  || i.title || i.service_name || "Service",
          short:    i.short || i.subtitle || i.category || "",
          subtitle: i.subtitle || i.short || i.category || "",
          price:    Number(i.price || i.amount || 0),
          image:    i.image || null,
          category: i.category || "",
          qty:      Number(i.qty || 1),
          bookingDate:   i.bookingDate   || "",
          bookingTime:   i.bookingTime   || "",
          bookingSlotId: i.bookingSlotId || "",
        })));
        setLocalCart(normalized);
      }
    } catch (e) { console.warn("[Cart] DB load failed:", e.message); }
    finally { setSectionLoading(false); }
  }, [currentMobile, eagerMobile, setLocalCart]);

  // ── One-time merge of pre-login local cart into MongoDB ──────────────────
  useEffect(() => {
    const mobile = eagerMobile;
    if (!hasMergedRef.current && isLoggedIn && mobile) {
      hasMergedRef.current = true;
      const localItems = readLocalCart();
      (async () => {
        if (localItems.length > 0) {
          try {
            await apiFetch(API.cartAdd(mobile), {
              method:"POST", body:JSON.stringify({ items: localItems }),
            });
            setToast(`${localItems.length} item(s) synced to your account`);
          } catch (e) { console.warn("[Cart] merge failed:", e.message); }
        }
        await loadDbCart(true);
      })();
    }
  }, [isLoggedIn, eagerMobile, loadDbCart]);

  // Redirect to home if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  // Open cart tab if navigated here with openCart flag
  useEffect(() => {
    if (location?.state?.openCart) {
      setActiveMenu("Cart");
      loadDbCart();
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line

  // Open Booked Services tab and force-refresh after reschedule
  useEffect(() => {
    if (location?.state?.openBookings) {
      setActiveMenu("Booked Services");
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line

  // Open My Membership tab after a successful membership purchase
  useEffect(() => {
    if (location?.state?.openMembership) {
      setActiveMenu("My Membership");
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line

  // Load membership data whenever that tab becomes active (covers both the
  // sidebar click path and the post-purchase redirect above). Addresses are
  // also loaded here since the redeem-a-visit modal needs them.
  useEffect(() => {
    if (activeMenu === "My Membership" && currentMobile) {
      loadMemberships(true);
      if (addresses.length === 0) loadAddresses();
    }
  }, [activeMenu, currentMobile]); // eslint-disable-line
  // When currentMobile resolves, check if a post-reschedule refresh is pending
  useEffect(() => {
    if (!currentMobile) return;
    if (sessionStorage.getItem("refresh_orders") !== "1") return;
    sessionStorage.removeItem("refresh_orders");
    localStorage.removeItem(`sched_orders_${currentMobile}`);
    localStorage.removeItem(`req_orders_${currentMobile}`);
    loadUserOrders(true);
    loadAllBookings(1);
  }, [currentMobile]); // eslint-disable-line

  // Initial data load
  useEffect(() => {
    (async () => {
      setPageLoading(true);
      await Promise.all([loadProfile(), loadOverview(), loadCancelConfig()]);
      setPageLoading(false);
    })();
  }, [eagerMobile, seedId]); // eslint-disable-line

  useEffect(() => {
    if (!profileLoading && (currentMobile || currentUserId)) { loadOverview(); loadCouponStatus(); }
  }, [profileLoading, currentMobile, currentUserId]); // eslint-disable-line

  // Keep local cart in sync with localStorage changes from other pages
  useEffect(() => {
    const handler = () => setLocalCartRaw(normalizeCart(readLocalCart()));
    window.addEventListener("cart-updated", handler);
    window.addEventListener("storage", handler);
    return () => { window.removeEventListener("cart-updated", handler); window.removeEventListener("storage", handler); };
  }, []);

  // Auto-refresh orders every 8 s while Booked Services tab is open.
  // Also refresh immediately when the browser tab regains visibility or focus —
  // so the status updates the moment the user switches back to this tab.
  useEffect(() => {
    if (activeMenu !== "Booked Services") return;

    const poll = () => loadUserOrders(true);

    // Interval: refresh every 8 seconds
    const id = setInterval(poll, 8_000);

    // Immediate refresh when tab becomes visible (user switched back)
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };

    // Immediate refresh when window regains focus
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", poll);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", poll);
    };
  }, [activeMenu, loadUserOrders]);

  // Load/reload the unified bookings feed whenever Booked Services is open
  // and whenever its filters (driven by the sub-tab clicked) change.
  useEffect(() => {
    if (activeMenu !== "Booked Services") return;
    loadAllBookings(1);
  }, [activeMenu, bookingFilters, loadAllBookings]);

  const handleCancelRequirement = useCallback(async (id) => {
    try {
      await apiFetch(API.cancelBabyCaretaker(id), { method: "PATCH" });
      setRequirementData((prev) =>
        prev.map((r) => r.id === id ? { ...r, status: 10, process_status: "cancelled" } : r)
      );
      localStorage.removeItem(`requirements_${currentMobile}`);
      setToast("Requirement cancelled.");
    } catch (e) { setToast("Cancel failed: " + (e.message || "Unknown error")); }
  }, [currentMobile]);

  const handleSidebarClick = async (item) => {
    setActiveMenu(item); setError("");
    if (item === "Booked Services")  { await loadBookings(); await loadUserOrders(true); }
    else if (item === "Cart")        await loadDbCart();
    else if (item === "My Requests") await loadRequirements();
    else if (item === "My Addresses") await loadAddresses();
    else if (item === "My Profile")  { await loadProfile(); await loadOverview(); }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try { await apiFetch(API.logout(), { method:"POST" }); } catch {}
    purgeCache(); ctxClearCart();
    window.dispatchEvent(new Event("auth-updated"));
    window.location.replace("/");
  };

  // ── CHECKOUT — the key fix ───────────────────────────────────────────────
  // Reads the freshest cart from localStorage (not stale React state),
  // normalises every item so SummaryPage always gets complete fields,
  // then navigates with both state AND localStorage in sync.
  const handleProceedToCheckout = useCallback(() => {
    // Always read fresh from localStorage so nothing is stale
    const freshCart = normalizeCart(readLocalCart());

    if (freshCart.length === 0) {
      setToast("Your cart is empty. Add services first.");
      return;
    }

    // Write normalised cart back so localStorage is clean
    writeLocalCart(freshCart);

    navigate("/checkout-schedule/summary", {
      state: {
        cartItems: freshCart,          // ← full normalised array
        user: {
          name:    userName,
          mobile:  currentMobile,
          email:   userEmail,
          address: profileUser?.default_address || profileUser?.address || "",
        },
      },
    });
  }, [userName, currentMobile, userEmail, profileUser, navigate]);

  const recentBookings = useMemo(
    () => [...overviewData.scheduledJobs].slice(0, 4),
    [overviewData]
  );

  const stats = useMemo(() => ({
    total:  overviewData.scheduledJobs.length,
    done:   overviewData.scheduledJobs.filter((i) => {
      const s = (i?.status || i?.job_status || "").toLowerCase();
      return s.includes("complete") || s.includes("done") || s.includes("success");
    }).length,
    points: overviewData.scheduledJobs.length * 25,
    pct:    pct(profileUser),
  }), [overviewData, profileUser]);

  if (pageLoading) {
    return (
      <div style={S.page}>
        <div style={S.loadBox}><div style={S.spinner} />Loading profile…</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {toast && <Toast msg={toast} onDone={() => setToast("")} />}

      <div style={S.layout}>
        {/* ── Sidebar ── */}
        <aside style={S.sidebar}>
          <div style={S.profileMini}>
            <img src={userAvatar} alt={userName} style={S.miniAvatar}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_AVATAR; }} />
            <div>
              <div style={S.miniName}>{userName}</div>
              <div style={S.miniMeta}>{currentMobile || userEmail}</div>
            </div>
          </div>

          <NavGroup items={MENU.main} active={activeMenu} onClick={handleSidebarClick} badge={cartCount} />

          {!couponLoading && couponStatus && (couponStatus.available_coupons || []).length > 0 && (
            <div style={S.sidebarCouponBox}>
              <div style={S.sidebarCouponTitle}>🎁 My Coupons</div>
              {couponStatus.available_coupons.map((c) => (
                <SidebarCouponMini key={c.coupon_code} c={c} />
              ))}
            </div>
          )}

          <button style={S.logoutBtn} onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? "Logging out..." : "Logout"}
          </button>
        </aside>

        {/* ── Main ── */}
        <main style={S.main}>
          {error && <div style={S.errBox}>{error}</div>}

          {/* MY PROFILE */}
          {activeMenu === "My Profile" && (
            <>
              <div style={S.heroRow}>
                <div style={S.heroCard}>
                  <div style={S.heroLeft}>
                    {profileLoading
                      ? <div style={S.avatarSkel} />
                      : <img src={userAvatar} alt={userName} style={S.heroAvatar}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_AVATAR; }} />
                    }
                    <div>
                      <h2 style={S.heroName}>{userName}</h2>
                      <div style={S.verifiedBadge}>✓ Verified User</div>
                      <div style={S.heroBio}>✉ {userEmail}</div>
                      <div style={S.heroBio}>📞 {currentMobile || "—"}</div>
                      <div style={S.heroBio}>📅 Member since {memberSince}</div>
                    </div>
                  </div>
                  <div style={S.heroStats}>
                    <Stat label="Total Bookings" v={stats.total} />
                    <Stat label="Completed"      v={stats.done} />
                    <Stat label="Points"         v={stats.points} />
                  </div>
                </div>
              </div>

              <div style={S.card}>
                <SH title="Recent Bookings" action="View All →" />
                <div style={S.listCard}>
                  {recentBookings.length === 0
                    ? <p style={S.empty}>No recent bookings.</p>
                    : recentBookings.map((item, idx) => <BRow key={item?.id || idx} item={item} />)
                  }
                </div>
              </div>

              <div style={S.card}>
                <SH title="Coupons" action="" />
                {couponLoading ? (
                  <p style={S.empty}>Loading coupons…</p>
                ) : !couponStatus || (couponStatus.available_coupons || []).length === 0 ? (
                  <p style={S.empty}>No coupons available right now.</p>
                ) : (
                  <div style={S.couponGrid}>
                    {couponStatus.available_coupons.map((c) => (
                      <CouponCard key={c.coupon_code} c={c} />
                    ))}
                  </div>
                )}
                {couponStatus && (
                  <div style={S.couponReferralNote}>
                    Referral rewards earned: {couponStatus.successful_referral_count}/{couponStatus.referral_coupon_total}
                    {" · "}Used: {couponStatus.referral_coupon_used}
                    {" · "}Remaining: {couponStatus.referral_coupon_remaining}
                  </div>
                )}
              </div>
            </>
          )}

          {/* BOOKED SERVICES */}
          {activeMenu === "Booked Services" && (() => {
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

                {/* ── Sub-tab switcher — each tab is a category/booking-type
                     preset against the unified bookings feed below ────── */}
                <div style={S.bookedTabRow}>
                  {[
                    { key:"all",        label:"All Bookings",    category:"all",       bookingType:"all" },
                    { key:"scheduled",  label:"Scheduled",       category:"all",       bookingType:"scheduled" },
                    { key:"home",       label:"Home",            category:"home",      bookingType:"all" },
                    { key:"technical",  label:"Technical",       category:"technical", bookingType:"all" },
                    { key:"special",    label:"Special Service", category:"special",   bookingType:"all" },
                    { key:"caretaker",  label:"Caretaker",       category:"caretaker", bookingType:"all" },
                  ].map(({ key, label, category, bookingType }) => (
                    <button
                      key={key}
                      style={{ ...S.bookedTabBtn, ...(bookedTab === key ? S.bookedTabActive : {}) }}
                      onClick={() => { setBookedTab(key); setBookingFilters({ category, bookingType, status:"all" }); }}
                    >
                      {label}
                    </button>
                  ))}
                  <button style={S.secLink} onClick={() => loadAllBookings(allBookingsPage.page)}>Refresh →</button>
                </div>

                {/* ── Bookings list (unified, paginated) ──────────────── */}
                <div style={S.card}>
                  {allBookingsLoading ? <SkelRows />
                    : allBookings.length === 0
                      ? <p style={S.empty}>No bookings found.</p>
                      : allBookings.map((item, idx) => (
                          <OrderCard
                            key={`${item?.order_id || item?._id || idx}`}
                            item={item}
                            orderType={item?.booking_type === "instant" ? "instant" : "scheduled"}
                            onCancel={item?.__readOnly ? undefined : handleCancelOrder}
                            onPay={item?.__readOnly ? undefined : handlePayOrder}
                            payingId={payingOrderId}
                            onConfirmJob={(item?.__readOnly && item?.category !== "special") ? undefined : handleConfirmJob}
                            confirmingId={confirmingJobId}
                          />
                        ))
                  }

                  {allBookingsPage.totalPages > 1 && (
                    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:12, marginTop:16 }}>
                      <button
                        style={S.secLink}
                        disabled={allBookingsPage.page <= 1}
                        onClick={() => loadAllBookings(allBookingsPage.page - 1)}
                      >
                        ← Prev
                      </button>
                      <span style={{ fontSize:13, color:"#6b7280" }}>
                        Page {allBookingsPage.page} of {allBookingsPage.totalPages} ({allBookingsPage.total} total)
                      </span>
                      <button
                        style={S.secLink}
                        disabled={allBookingsPage.page >= allBookingsPage.totalPages}
                        onClick={() => loadAllBookings(allBookingsPage.page + 1)}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Cancel confirmation modal ─────────────────────── */}
                <CancelModal
                  modal={cancelModal}
                  onConfirm={confirmCancelOrder}
                  onClose={() => setCancelModal(null)}
                />

                {/* ── Payment result modal ───────────────────────────── */}
                <PayModal
                  modal={payModal}
                  onClose={() => setPayModal(null)}
                />

                {/* ── Pay-after-work payment modal ───────────────────── */}
                <PaymentDueModal
                  modal={paymentDueModal}
                  onPay={handlePayOrder}
                  onClose={() => setPaymentDueModal(null)}
                  payingOrderId={payingOrderId}
                />
              </div>
            );
          })()}

          {/* MY REQUESTS */}
          {activeMenu === "My Requests" && (() => {
            const pending = requirementData.filter(r => r.status === 0);
            return (
              <div style={S.card}>
                <div style={S.secHead}>
                  <h2 style={S.secTitle}>My Requests</h2>
                  <button style={S.secLink} onClick={() => loadRequirements(true)}>Refresh →</button>
                </div>
                {sectionLoading ? <SkelRows />
                  : pending.length === 0
                    ? <p style={S.empty}>No pending requests. Assigned or closed requests appear in Booked Services.</p>
                    : pending.map((item, idx) => (
                        <BabyCaretakerCard
                          key={item?.id || idx}
                          item={item}
                          onCancel={handleCancelRequirement}
                        />
                      ))
                }
              </div>
            );
          })()}

          {/* MY MEMBERSHIP */}
          {activeMenu === "My Membership" && (
            <div style={S.card}>
              <div style={S.secHead}>
                <h2 style={S.secTitle}>My Membership</h2>
                <button style={S.secLink} onClick={() => loadMemberships(true)}>Refresh →</button>
              </div>
              {sectionLoading ? <SkelRows />
                : memberships.length === 0
                  ? (
                    <p style={S.empty}>
                      No active membership yet.{" "}
                      <a href="/subscription-plans" style={{ color: "#4f46e5", fontWeight: 700 }}>Browse plans →</a>
                    </p>
                  )
                  : (
                    <div style={S.membGrid}>
                      {memberships.map((m) => (
                        <MembershipCard
                          key={m.membership_id}
                          membership={m}
                          onDetailView={() => setDetailModal(m)}
                          onCancel={() => handleCancelMembership(m)}
                        />
                      ))}
                    </div>
                  )
              }

              {/* ── Detail Service: day-by-day schedule popup ────────── */}
              <MembershipScheduleModal
                membership={detailModal}
                onClose={() => setDetailModal(null)}
              />

              {/* ── Cancel membership confirmation modal ──────────────── */}
              <MembershipCancelModal
                membership={membershipCancelModal}
                onConfirm={confirmCancelMembership}
                onClose={() => setMembershipCancelModal(null)}
              />
            </div>
          )}

          {/* MY ADDRESSES */}
          {activeMenu === "My Addresses" && (
            <AddressSection
              addresses={addresses}
              saving={addrSaving}
              onSave={saveAddresses}
              onRefresh={() => loadAddresses(true)}
            />
          )}

          {/* CART */}
          {activeMenu === "Cart" && (
            <div>
              <div style={S.card}>
                <div style={S.secHead}>
                  <h2 style={S.secTitle}>My Cart {cartCount > 0 && `(${cartCount})`}</h2>
                  {localCart.length > 0 && (
                    <button style={S.clearBtn} onClick={handleClearCart}>Clear All</button>
                  )}
                </div>

                {localCart.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"40px 20px" }}>
                    <div style={{ fontSize:42, marginBottom:12 }}>🛒</div>
                    <p style={S.empty}>Your cart is empty.</p>
                    <button style={S.addBtn} onClick={() => navigate("/services/all-services")}>
                      Browse Services
                    </button>
                  </div>
                ) : (
                  <>
                    {localCart.map((item, i) => (
                      <CartRow key={item.id || i} item={item}
                        onQty={handleQtyChange} onRemove={handleRemoveFromCart} />
                    ))}

                    {/* Bill summary */}
                    <div style={S.cartBill}>
                      <div style={S.billRow}><span>Subtotal</span><span>{fmt(cartTotal)}</span></div>
                      <div style={S.billRow}>
                        <span>Discount (5%)</span>
                        <span style={{ color:"#16a34a", fontWeight:700 }}>-{fmt(Math.round(cartTotal*0.05))}</span>
                      </div>
                      <div style={S.billRow}>
                        <span>Taxes (6%)</span>
                        <span>{fmt(Math.round(cartTotal*0.06))}</span>
                      </div>
                      <div style={S.billTotal}>
                        <span>Total</span>
                        <span>{fmt(cartTotal - Math.round(cartTotal*0.05) + Math.round(cartTotal*0.06))}</span>
                      </div>
                    </div>

                    <div style={S.cartFoot}>
                      <button style={S.proceedBtn} onClick={handleProceedToCheckout}>
                        Proceed to Checkout →
                      </button>
                    </div>
                  </>
                )}
              </div>

            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const NavGroup = ({ items, active, onClick, badge = 0 }) => (
  <div style={S.navGroup}>
    {items.map((item) => (
      <button key={item} onClick={() => onClick(item)}
        style={{ ...S.navItem, ...(item === active ? S.navActive : {}) }}>
        {item}
        {item === "Cart" && badge > 0 && <span style={S.navBadge}>{badge}</span>}
      </button>
    ))}
  </div>
);

const Stat = ({ label, v }) => (
  <div><div style={S.statLabel}>{label}</div><div style={S.statVal}>{v}</div></div>
);

const SH = ({ title, action }) => (
  <div style={S.secHead}>
    <h3 style={S.secTitle}>{title}</h3>
    <button style={S.secLink}>{action}</button>
  </div>
);

const BRow = ({ item }) => (
  <div style={S.bookRow}>
    <img src={item?.service_image || FALLBACK_IMG} alt="svc" style={S.bookImg}
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }} />
    <div style={{ flex:1 }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>{item?.service_name || item?.title || "Service"}</div>
      <div style={{ fontSize:13, color:"#6b7280" }}>{item?.scheduled_date || item?.date || "No date"}</div>
    </div>
    <div style={{ textAlign:"right" }}>
      <div style={S.statusBadge}>{item?.status || item?.job_status || "Scheduled"}</div>
      <div style={{ fontWeight:800 }}>{fmt(item?.amount || item?.price || 0)}</div>
    </div>
  </div>
);

// ── Coupons ───────────────────────────────────────────────────────────────
const formatCouponExpiry = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const CouponCard = ({ c }) => (
  <div style={S.couponCard}>
    <div style={S.couponCardTop}>
      <span style={S.couponBadge}>{c.discount_percent}% OFF</span>
      {c.expires_at && <span style={S.couponExpiry}>Valid till {formatCouponExpiry(c.expires_at)}</span>}
    </div>
    <div style={S.couponCode}>{c.coupon_code}</div>
  </div>
);

const SidebarCouponMini = ({ c }) => (
  <div style={S.sidebarCouponCard}>
    <span style={S.sidebarCouponCode}>{c.coupon_code}</span>
    <span style={S.sidebarCouponPct}>{c.discount_percent}%</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ORDER CARD — for scheduled_order / requirement_order with cancel button
// ─────────────────────────────────────────────────────────────────────────────
const ORDER_STATUS = {
  0: { label: "New Order",            color: "#92400e", bg: "#fef3c7" },
  1: { label: "Accepted",             color: "#1e40af", bg: "#dbeafe" },
  2: { label: "Work Started",         color: "#c2410c", bg: "#ffedd5" },
  3: { label: "Work Done",            color: "#6d28d9", bg: "#ede9fe" },   // partner marked done — awaiting user confirm
  4: { label: "Declined",             color: "#991b1b", bg: "#fee2e2" },
  5: { label: "Cancelled by You",     color: "#6b7280", bg: "#f3f4f6" },
  6: { label: "Cancelled by Partner", color: "#6b7280", bg: "#f3f4f6" },
  7: { label: "Payment Received",     color: "#0369a1", bg: "#e0f2fe" },
  8: { label: "Job Closed",           color: "#14532d", bg: "#dcfce7" },
  9: { label: "Payment Due",          color: "#b45309", bg: "#fef3c7" },   // user confirmed, payment pending
  777:              { label: "Pending",            color: "#92400e", bg: "#fef3c7" },
  10:               { label: "Cancelled",          color: "#6b7280", bg: "#f3f4f6" },
  // Instant order codes (780-785) — surfaced here for the first time via
  // the unified Booked Services feed (see loadAllBookings); without these
  // the raw numeric code (e.g. "785") would render as the badge text.
  780:              { label: "On The Way",         color: "#1e40af", bg: "#dbeafe" },
  781:              { label: "Partner Arrived",     color: "#c2410c", bg: "#ffedd5" },
  782:              { label: "Work Done",           color: "#6d28d9", bg: "#ede9fe" },
  783:              { label: "Job Closed",          color: "#14532d", bg: "#dcfce7" },
  784:              { label: "No Partner Found",    color: "#6b7280", bg: "#f3f4f6" },
  785:              { label: "Cancelled",           color: "#6b7280", bg: "#f3f4f6" },
  // String keys
  new:                  { label: "New Order",            color: "#92400e", bg: "#fef3c7" },
  accepted:             { label: "Accepted",             color: "#1e40af", bg: "#dbeafe" },
  started:              { label: "Work Started",         color: "#c2410c", bg: "#ffedd5" },
  completed:            { label: "Work Done",            color: "#6d28d9", bg: "#ede9fe" },
  user_confirmed:       { label: "Payment Due",          color: "#b45309", bg: "#fef3c7" },
  payment_received:     { label: "Payment Received",     color: "#0369a1", bg: "#e0f2fe" },
  job_closed:           { label: "Job Closed",           color: "#14532d", bg: "#dcfce7" },
  paid:                 { label: "Payment Received",     color: "#0369a1", bg: "#e0f2fe" },
  declined:             { label: "Declined",             color: "#991b1b", bg: "#fee2e2" },
  cancelled_by_user:    { label: "Cancelled by You",     color: "#6b7280", bg: "#f3f4f6" },
  cancelled_by_partner: { label: "Cancelled by Partner", color: "#6b7280", bg: "#f3f4f6" },
  pending:              { label: "Pending",              color: "#92400e", bg: "#fef3c7" },
  ongoing:              { label: "Accepted",             color: "#1e40af", bg: "#dbeafe" },
  cancelled:            { label: "Cancelled",            color: "#6b7280", bg: "#f3f4f6" },
};

const getOrderStatus = (raw) => {
  if (raw === undefined || raw === null) return ORDER_STATUS[777];
  const num = Number(raw);
  if (!isNaN(num) && ORDER_STATUS[num] !== undefined) return ORDER_STATUS[num];
  const key = String(raw).toLowerCase().replace(/\s+/g, "_");
  return ORDER_STATUS[key] || { label: String(raw || "Pending"), color: "#374151", bg: "#f3f4f6" };
};

const OrderCard = ({ item, orderType, onCancel, onPay, payingId, onConfirmJob, confirmingId, onReschedule }) => {
  const [expanded, setExpanded] = useState(false);
  const st         = getOrderStatus(item?.status);
  const numStatus  = Number(item?.status);
  const rawStatus  = String(item?.status || "").toLowerCase();
  const payMode    = item?.payment_mode || "pay_now";
  const docId      = item?._id || item?.order_id;

  const isWorkDone        = numStatus === 3;
  const isUserConfirmed   = numStatus === 9;
  const isPaymentReceived = numStatus === 7;
  const isJobClosed       = numStatus === 8;
  const isPaid            = isPaymentReceived || isJobClosed;
  const isCancelled          = [4, 5, 6, 10].includes(numStatus) ||
    ["declined","cancelled","cancelled_by_user","cancelled_by_partner"].includes(rawStatus);
  const isCancelledByPartner = numStatus === 6 || rawStatus === "cancelled_by_partner";
  const canCancel         = !isCancelled && !isWorkDone && !isUserConfirmed &&
                            !isPaymentReceived && !isJobClosed && numStatus < 3;

  const isThisPaying     = payingId     && (payingId     === docId);
  const isThisConfirming = confirmingId && (confirmingId === docId);

  // Support both service-page orders (selected_services) and cart orders (items array)
  // Support three sources for the services list:
  //   1. item.services      — new array format ["Mopping","Stove Cleaning"] (cart orders)
  //   2. item.selected_services — service-page orders
  //   3. item.items         — cart order item objects
  const rawServices = (() => {
    if (Array.isArray(item?.services) && item.services.length > 0)
      return item.services.map(s => ({ service: s }));
    if (Array.isArray(item?.selected_services) && item.selected_services.length > 0)
      return item.selected_services;
    if (Array.isArray(item?.items) && item.items.length > 0)
      return item.items;
    return [];
  })();

  // Normalise each row to a common shape
  const services = rawServices.map(sv => ({
    name:  sv?.service     || sv?.label || sv?.title || sv?.name || (typeof sv === "string" ? sv : ""),
    sub:   sv?.description || sv?.subtitle || sv?.short || "",
    price: sv?.price    != null ? sv.price    : (sv?.line_total != null ? sv.line_total : null),
    qty:   sv?.qty      != null ? sv.qty      : null,
  }));

  const basePrice = parseFloat(
    item?.service_charge || item?.total_amount || item?.amount || item?.price || 0
  );

  // service_category may now be an array ["home_service"] or a plain string
  const categorySlug = Array.isArray(item?.service_category)
    ? item.service_category[0] || ""
    : item?.service_category || "";

  // Human display title: top-level service string → first item name → fallback
  const firstItemName  = services[0]?.name || "";
  const extraCount     = services.length > 1 ? ` +${services.length - 1} more` : "";
  const displayTitle   = item?.service || (firstItemName ? firstItemName + extraCount : "Service Order");

  // For cart orders item.services is string[] → rawServices has no booking_date.
  // Fall back to item.items[0] which always carries booking_date/booking_time.
  const firstItemObj = Array.isArray(item?.items) && item.items.length > 0 ? item.items[0] : null;
  const displayDate = item?.date || item?.booking_date
    || firstItemObj?.booking_date
    || rawServices[0]?.booking_date || "";
  const displaySlot = item?.slot || item?.booking_time
    || firstItemObj?.booking_time || firstItemObj?.booking_slot
    || rawServices[0]?.booking_time || rawServices[0]?.booking_slot || "";
  const categoryDisplay = categorySlug
    ? categorySlug.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : "";

  const partner  = item?.partner_info || null;
  const STEPS    = [
    { label: "Placed",      s: 0, icon: "📝" },
    { label: "Accepted",    s: 1, icon: "🤝" },
    { label: "In Progress", s: 2, icon: "🔧" },
    { label: "Work Done",   s: 3, icon: "🎉" },
  ];
  const showSteps = numStatus >= 0 && numStatus <= 3 && !isCancelled;

  const cancelReason =
    numStatus === 5 ? "cancelled by you"
    : numStatus === 6 ? "cancelled by partner"
    : numStatus === 4 ? "declined by partner"
    : "cancelled";

  return (
    <div style={S.ocCard}>

      {/* ── Top row: title / id  +  status badge / amount ── */}
      <div style={S.ocTopRow}>
        <div style={{ flex:1, minWidth:0 }}>
          <span style={S.ocTitle}>{displayTitle}</span>
          <div style={S.ocOrderId}>
            #{item?.order_id ? String(item.order_id).slice(-8) : (item?._id ? String(item._id).slice(-8) : "—")}
          </div>
          {(categoryDisplay || displayDate || displaySlot) && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:7 }}>
              {categoryDisplay && <span style={S.ocCatChip}>{categoryDisplay}</span>}
              {displayDate     && <span style={S.ocBookingChip}>📅 {displayDate}</span>}
              {displaySlot     && <span style={S.ocBookingChip}>🕐 {displaySlot}</span>}
            </div>
          )}
        </div>
        <div style={S.ocRightTop}>
          <span style={{ ...S.ocBadge, color:st.color, background:st.bg }}>{st.label}</span>
          <div style={S.ocAmount}>{fmt(basePrice)}</div>
        </div>
      </div>

      {/* ── Progress cards (status 0–3 only) ── */}
      {showSteps && (
        <div style={S.ocStepsGrid}>
          {STEPS.map(({ label, s, icon }) => {
            const done   = numStatus > s;
            const isCurr = numStatus === s;
            return (
              <div
                key={s}
                style={{
                  ...S.ocStepCard,
                  ...(done ? S.ocStepCardDone : {}),
                  ...(isCurr ? S.ocStepCardCurrent : {}),
                }}
              >
                <div
                  style={{
                    ...S.ocStepIconWrap,
                    ...(done ? S.ocStepIconWrapDone : {}),
                    ...(isCurr ? S.ocStepIconWrapCurrent : {}),
                  }}
                >
                  <span style={{ fontSize: isCurr ? 20 : 16 }}>{done ? "✅" : icon}</span>
                </div>
                <span
                  style={{
                    ...S.ocStepCardLabel,
                    ...(done ? S.ocStepCardLabelDone : {}),
                    ...(isCurr ? S.ocStepCardLabelCurrent : {}),
                  }}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Partner strip (status 1–6) ── */}
      {numStatus >= 1 && numStatus <= 6 && partner?.name && (
        <div style={S.ocPartnerCard}>
          <div style={S.ocPartnerAvatar}>
            <span style={{ color:"#fff", fontWeight:800, fontSize:18 }}>
              {partner.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#1e3a8a" }}>{partner.name}</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"2px 10px", marginTop:3, fontSize:11, color:"#6b7280" }}>
              {partner.mobile     && <span>📞 {partner.mobile}</span>}
              {partner.experience && <span>⏱ {partner.experience}</span>}
              {partner.city       && <span>📍 {partner.city}</span>}
            </div>
          </div>
          <span style={S.ocPartnerBadge}>Assigned</span>
        </div>
      )}

      {/* ── Service details box ── */}
      {services.length > 0 && (
        <div style={S.ocDetailsBox}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={S.ocDetailsLabel}>
              {services.length === 1 ? "Service" : `${services.length} Services`}
            </span>
            {services.length > 1 && (
              <button style={S.ocExpandBtn} onClick={() => setExpanded(e => !e)}>
                {expanded ? "Hide ▲" : "Show all ▼"}
              </button>
            )}
          </div>
          {!expanded ? (
            <div style={S.ocDetailsValue}>{services[0]?.name || displayTitle}</div>
          ) : (
            <div style={S.ocServiceList}>
              {services.map((sv, i) => (
                <div key={i} style={S.ocServiceRow}>
                  <span>
                    {sv.name || `Item ${i + 1}`}
                    {sv.sub && <span style={{ color:"#6b7280", fontSize:12 }}> — {sv.sub}</span>}
                    {sv.qty != null && sv.qty > 1 && <span style={{ color:"#6b7280" }}> ×{sv.qty}</span>}
                  </span>
                  {sv.price != null && <span style={{ fontWeight:700 }}>{fmt(sv.price)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Cancellation charge note ── */}
      {isCancelled && item?.cancellation_charge != null && (
        <div style={S.ocCancelledNote}>
          Cancellation charge: <strong>{fmt(item.cancellation_charge)}</strong>
        </div>
      )}

      {/* ── Paid pill ── */}
      {isPaid && item?.payments?.length > 0 && (
        <div style={S.ocPaidPill}>
          ✓ Paid · Txn: {item.payments[item.payments.length - 1]?.transaction_id || "—"}
        </div>
      )}

      {/* ── Cancelled info box ── */}
      {isCancelled && (
        <div style={S.ocCancelInfoBox}>
          <span style={{ fontSize:12, color:"#6b7280" }}>
            This order has been {cancelReason}.
          </span>
          {isCancelledByPartner && onReschedule && (
            <button
              style={S.ocRescheduleBtn}
              onClick={() => onReschedule(item)}
            >
              🔄 Reschedule
            </button>
          )}
        </div>
      )}

      {/* ── Work done — user must confirm ── */}
      {isWorkDone && (
        <div style={S.ocActions}>
          <div style={S.ocWorkDoneBanner}>
            🔔 Partner has marked this job as done.{onConfirmJob ? " Please confirm to proceed." : ""}
          </div>
          {onConfirmJob && (
            <button
              style={{ ...S.ocPayBtn, marginTop:8, opacity: isThisConfirming ? 0.6 : 1 }}
              disabled={!!isThisConfirming}
              onClick={() => onConfirmJob(item)}
            >
              {isThisConfirming
                ? "Processing…"
                : payMode === "pay_after_work"
                  ? "Confirm Done & Pay"
                  : "Confirm Job Done"}
            </button>
          )}
        </div>
      )}

      {/* ── Payment due (pay_after_work, user confirmed) ── */}
      {isUserConfirmed && (
        <div style={S.ocPaymentDueBox}>
          <div style={S.ocPaymentDueText}>
            💰 Payment due · {fmt(item?.total_amount || 0)}
          </div>
          {onPay && (
            <button
              style={{ ...S.ocPayBtn, marginTop:8, background:"#d97706", opacity: isThisPaying ? 0.6 : 1 }}
              disabled={!!isThisPaying}
              onClick={() => onPay(item)}
            >
              {isThisPaying ? "Processing…" : `Pay ${fmt(item?.total_amount || 0)}`}
            </button>
          )}
        </div>
      )}

      {/* ── Payment received — waiting for partner to close ── */}
      {isPaymentReceived && (
        <div style={S.ocPaymentReceivedBox}>
          ✓ Payment received — partner will close the job shortly
        </div>
      )}

      {/* ── Job closed — done ── */}
      {isJobClosed && (
        <div style={S.ocJobClosedBox}>
          ✅ Job completed and closed. Thank you!
        </div>
      )}

      {/* ── Cancel (only for active unstarted orders) ── */}
      {canCancel && onCancel && (
        <div style={S.ocActions}>
          <button style={S.ocCancelBtn} onClick={() => onCancel(item, orderType)}>
            Cancel Order
          </button>
        </div>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CANCEL MODAL
// ─────────────────────────────────────────────────────────────────────────────
const CancelModal = ({ modal, onConfirm, onClose }) => {
  if (!modal) return null;
  const { order, info } = modal;
  const { charge, chargePercent, conditionLabel, offerDiscount, netCharge, basePrice, appliedOffer } = info;

  return (
    <div style={S.modalOverlay}>
      <div style={S.modalBox}>
        <h3 style={S.modalTitle}>Cancel Order</h3>
        <p style={S.modalSub}>{order?.service || order?.service_category || "Service"}</p>

        {/* Cancellation condition */}
        <div style={S.modalRow}>
          <span style={S.modalLabel}>Stage</span>
          <span style={S.modalVal}>{conditionLabel || "—"}</span>
        </div>

        {/* Order value */}
        <div style={S.modalRow}>
          <span style={S.modalLabel}>Order value</span>
          <span style={S.modalVal}>{fmt(basePrice)}</span>
        </div>

        {/* Cancellation charge */}
        <div style={{ ...S.modalRow, marginTop:8, paddingTop:8, borderTop:"1px solid #e5e7eb" }}>
          <span style={S.modalLabel}>
            Cancellation charge
            {chargePercent > 0 && <span style={{ fontSize:11, color:"#9ca3af", marginLeft:4 }}>({chargePercent}%)</span>}
          </span>
          <span style={{ ...S.modalVal, color: charge > 0 ? "#dc2626" : "#16a34a", fontWeight:800 }}>
            {charge === 0 ? "Free" : fmt(charge)}
          </span>
        </div>

        {/* Offer discount */}
        {offerDiscount > 0 && (
          <div style={S.modalRow}>
            <span style={S.modalLabel}>Offer discount</span>
            <span style={{ ...S.modalVal, color:"#16a34a", fontWeight:700 }}>
              − {fmt(offerDiscount)}
              {appliedOffer && <span style={{ fontSize:11, marginLeft:6, color:"#6b7280" }}>({appliedOffer})</span>}
            </span>
          </div>
        )}

        {/* Net charge */}
        <div style={{ ...S.modalRow, paddingTop:10, borderTop:"2px solid #e5e7eb", marginTop:6 }}>
          <span style={{ ...S.modalLabel, fontWeight:800, color:"#111827" }}>You pay</span>
          <span style={{ fontSize:20, fontWeight:800, color: netCharge > 0 ? "#dc2626" : "#16a34a" }}>
            {netCharge === 0 ? "₹0 (Free)" : fmt(netCharge)}
          </span>
        </div>

        <div style={S.modalBtnRow}>
          <button style={S.modalKeepBtn} onClick={onClose}>Keep Order</button>
          <button style={S.modalConfirmBtn} onClick={onConfirm}>Confirm Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAY MODAL — shown after successful dummy payment
// ─────────────────────────────────────────────────────────────────────────────
const PayModal = ({ modal, onClose }) => {
  if (!modal) return null;
  const { result } = modal;
  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 36, textAlign: "center", marginBottom: 8 }}>✅</div>
        <div style={{ ...S.modalTitle, textAlign: "center", color: "#0369a1" }}>Payment Successful</div>
        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          {[
            ["Amount Paid",      fmt(result?.amount)],
            ["Payment ID",       result?.payment_id   || "—"],
            ["Transaction ID",   result?.transaction_id || "—"],
            ["Currency",         result?.currency     || "INR"],
            ["Status",           result?.status       || "success"],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: "#6b7280" }}>{label}</span>
              <span style={{ fontWeight: 700, wordBreak: "break-all", maxWidth: "60%", textAlign: "right" }}>{val}</span>
            </div>
          ))}
        </div>
        <button
          style={{ ...S.modalConfirmBtn, width: "100%", marginTop: 20, background: "#0369a1" }}
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAY-AFTER-WORK PAYMENT MODAL
// ─────────────────────────────────────────────────────────────────────────────
const PaymentDueModal = ({ modal, onPay, onClose, payingOrderId }) => {
  if (!modal) return null;
  const { order } = modal;
  const docId     = order?._id || order?.order_id;
  const isPaying  = payingOrderId && payingOrderId === docId;
  const amount    = Number(order?.total_amount || 0);
  const fmt       = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const serviceName = order?.service || order?.service_name || "Service Booking";
  const orderId     = String(order?.order_id || order?._id || "").slice(-8).toUpperCase();

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 40, textAlign: "center", marginBottom: 8 }}>💳</div>
        <div style={{ ...S.modalTitle, textAlign: "center", color: "#1d4ed8" }}>
          Complete Payment
        </div>
        <p style={{ textAlign: "center", color: "#6b7280", fontSize: 14, margin: "8px 0 20px" }}>
          You confirmed the job is done. Please pay to finish the booking.
        </p>

        <div style={{ display:"grid", gap:10, background:"#f8fafc", padding:"14px", borderRadius:12, marginBottom:20 }}>
          {orderId && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
              <span style={{ color:"#6b7280" }}>Order #</span>
              <span style={{ fontWeight:700 }}>{orderId}</span>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13 }}>
            <span style={{ color:"#6b7280" }}>Service</span>
            <span style={{ fontWeight:700, maxWidth:"60%", textAlign:"right" }}>{serviceName}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:20, fontWeight:900, paddingTop:10, borderTop:"1px solid #e5e7eb" }}>
            <span>Total Due</span>
            <span style={{ color:"#1d4ed8" }}>{fmt(amount)}</span>
          </div>
        </div>

        <button
          style={{ ...S.modalConfirmBtn, width:"100%", background:"#1d4ed8", opacity: isPaying ? 0.7 : 1 }}
          disabled={!!isPaying}
          onClick={() => onPay(order)}
        >
          {isPaying ? "Processing…" : `Pay ${fmt(amount)}`}
        </button>
        <button
          style={{ width:"100%", marginTop:10, height:44, borderRadius:12, border:"1px solid #d1d5db", background:"#fff", color:"#6b7280", fontWeight:700, cursor:"pointer", fontSize:14 }}
          onClick={onClose}
        >
          Pay Later
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MEMBERSHIP — plan card + Detail Service schedule popup
// ─────────────────────────────────────────────────────────────────────────────
const MembershipCard = ({ membership: m, onDetailView, onCancel }) => {
  const [showHistory, setShowHistory] = useState(false);

  const isActive     = m.status === "active" && !m.is_expired;
  const isUnlimited  = m.visits_total == null;
  const visits        = Array.isArray(m.visits) ? m.visits : [];
  const completedCount = visits.filter((v) => v.status === "completed").length;

  const statusMeta = isActive
    ? { label: "Active",  color: "#15803d", bg: "#ecfdf3" }
    : { label: m.status === "expired" || m.is_expired ? "Expired" : "Inactive", color: "#6b7280", bg: "#f3f4f6" };

  return (
    <div style={S.membCard}>
      <div style={S.membTop}>
        <div>
          <div style={S.membName}>{m.plan_name}</div>
          <div style={S.membSub}>Activated {m.activated_date || m.start_date} · Valid until {m.expiry_date}</div>
        </div>
        <span style={{ ...S.membBadge, color: statusMeta.color, background: statusMeta.bg }}>{statusMeta.label}</span>
      </div>

      <div style={S.membStatsRow}>
        <div style={S.membStat}>
          <div style={S.membStatVal}>{isUnlimited ? "∞" : m.visits_remaining}</div>
          <div style={S.membStatLabel}>{isUnlimited ? "Unlimited" : `of ${m.visits_total} left`}</div>
        </div>
        <div style={S.membStat}>
          <div style={S.membStatVal}>{completedCount}</div>
          <div style={S.membStatLabel}>Completed</div>
        </div>
        <div style={S.membStat}>
          <div style={S.membStatVal}>₹{Number(m.price_paid || 0).toLocaleString("en-IN")}</div>
          <div style={S.membStatLabel}>Paid</div>
        </div>
      </div>

      <div style={S.membBtnRow}>
        <button style={{ ...S.membDetailBtn, flex: 1 }} onClick={onDetailView}>
          Detail Service →
        </button>
        {isActive && (
          <button style={S.membCancelBtn} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>

      {visits.length > 0 && (
        <>
          <button style={S.membHistoryToggle} onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? "Hide" : "Show"} Visit History ({visits.length}) {showHistory ? "▲" : "▼"}
          </button>
          {showHistory && (
            <div style={S.membHistoryList}>
              {visits.slice().reverse().map((v) => {
                const done = v.status === "completed";
                return (
                  <div key={v.processing_id || v.order_id} style={S.membVisitRow}>
                    <div style={S.membVisitLeft}>
                      <div style={S.membVisitService}>{v.service}{v.service_category ? ` · ${v.service_category}` : ""}</div>
                      <div style={S.membVisitMeta}>
                        {v.date || "—"} {v.time_slot ? `· ${v.time_slot}` : ""}
                      </div>
                      <div style={S.membVisitMeta}>
                        {v.partner?.name ? `Partner: ${v.partner.name}` : "Partner: not yet assigned"}
                      </div>
                      <div style={S.membVisitId}>Job ID: {v.processing_id || "—"}</div>
                    </div>
                    <span style={{ ...S.membVisitBadge, color: done ? "#15803d" : "#92400e", background: done ? "#ecfdf3" : "#fef3c7" }}>
                      {done ? "Completed" : "Pending"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cancel membership — confirmation modal. Confirming removes every
// pending/assigned/completed day-of-work record for this plan and
// soft-cancels the order (see cancel_membership in AppBackend/views.py).
// ─────────────────────────────────────────────────────────────────────────────
const MembershipCancelModal = ({ membership: m, onConfirm, onClose }) => {
  if (!m) return null;
  const visits = Array.isArray(m.visits) ? m.visits : [];
  const remaining = m.visits_total == null ? "Unlimited" : (m.visits_remaining ?? 0);

  return (
    <div style={S.modalOverlay}>
      <div style={S.modalBox}>
        <h3 style={S.modalTitle}>Cancel Membership</h3>
        <p style={S.modalSub}>{m.plan_name}</p>

        <div style={S.modalRow}>
          <span style={S.modalLabel}>Visits remaining</span>
          <span style={S.modalVal}>{remaining}</span>
        </div>
        <div style={S.modalRow}>
          <span style={S.modalLabel}>Valid until</span>
          <span style={S.modalVal}>{m.expiry_date || "—"}</span>
        </div>

        <p style={{ fontSize:13, color:"#dc2626", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 12px", marginTop:14, lineHeight:1.5 }}>
          This cancels the plan immediately — any upcoming scheduled visits
          {visits.length > 0 ? " and assigned partners " : " "}
          for this membership will be removed. This can't be undone.
        </p>

        <div style={S.modalBtnRow}>
          <button style={S.modalKeepBtn} onClick={onClose}>Keep Membership</button>
          <button style={S.modalConfirmBtn} onClick={onConfirm}>Confirm Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// "Detail Service" popup — plan basics + every scheduled visit date for
// this membership,
// fetched live from subscription_weekly / subscription_monthly (whichever
// applies) via API.membershipSchedule(). Rest days (monthly-care's 5-work/
// 1-rest gaps) simply have no record, so they never show up here.
// ─────────────────────────────────────────────────────────────────────────────
const SCHEDULE_STATUS_META = {
  pending:   { label: "Pending",   color: "#92400e", bg: "#fef3c7" },
  assigned:  { label: "Assigned",  color: "#1d4ed8", bg: "#dbeafe" },
  completed: { label: "Completed", color: "#15803d", bg: "#ecfdf3" },
};

const MembershipScheduleModal = ({ membership, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [days,    setDays]    = useState([]);

  useEffect(() => {
    if (!membership) return;
    let cancelled = false;
    setLoading(true); setError(""); setDays([]);
    apiFetch(API.membershipSchedule(membership.membership_id))
      .then((data) => { if (!cancelled) setDays(Array.isArray(data?.data) ? data.data : []); })
      .catch((e) => { if (!cancelled) setError(e.message || "Failed to load schedule."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [membership]);

  if (!membership) return null;

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 480, maxHeight: "88vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...S.modalTitle, marginBottom: 4 }}>{membership.plan_name}</div>
        <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 14px" }}>
          {days.length > 0 ? `${days.length} scheduled visit${days.length === 1 ? "" : "s"}` : " "}
        </p>

        {/* Basic info — time slot, start date, customer, price */}
        <div style={S.membInfoGrid}>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Start Date</div>
            <div style={S.membInfoVal}>{membership.activated_date || membership.start_date || "—"}</div>
          </div>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Time Slot</div>
            <div style={S.membInfoVal}>{membership.preferred_slot || "—"}</div>
          </div>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Valid Until</div>
            <div style={S.membInfoVal}>{membership.expiry_date || "—"}</div>
          </div>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Amount Paid</div>
            <div style={S.membInfoVal}>₹{Number(membership.price_paid || 0).toLocaleString("en-IN")}</div>
          </div>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Customer</div>
            <div style={S.membInfoVal}>{membership.customer?.name || membership.mobile || "—"}</div>
          </div>
          <div style={S.membInfoItem}>
            <div style={S.membInfoLabel}>Mobile</div>
            <div style={S.membInfoVal}>{membership.customer?.mobile || membership.mobile || "—"}</div>
          </div>
          <div style={{ ...S.membInfoItem, gridColumn: "1 / -1" }}>
            <div style={S.membInfoLabel}>Address</div>
            <div style={S.membInfoVal}>
              {[membership.delivery_address?.line1, membership.delivery_address?.area, membership.delivery_address?.city]
                .filter(Boolean).join(", ") || "—"}
            </div>
          </div>
        </div>

        <div style={S.membScheduleHead}>Visit Schedule</div>

        {loading ? (
          <p style={{ textAlign: "center", color: "#6b7280", padding: "20px 0" }}>Loading schedule…</p>
        ) : error ? (
          <p style={{ textAlign: "center", color: "#dc2626", padding: "20px 0" }}>{error}</p>
        ) : days.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6b7280", padding: "20px 0" }}>No scheduled visits found yet.</p>
        ) : (
          <div style={S.membScheduleList}>
            {days.map((d) => {
              const meta = SCHEDULE_STATUS_META[d.status] || SCHEDULE_STATUS_META.pending;
              const num  = d.visit_number ?? d.day_number;
              return (
                <div key={d._id || d.date} style={S.membScheduleRow}>
                  <div>
                    <div style={S.membScheduleDate}>{d.date}</div>
                    <div style={S.membScheduleMeta}>
                      {num ? `Visit ${num}` : ""}{d.preferred_slot ? ` · ${d.preferred_slot}` : ""}
                    </div>
                    <div style={S.membScheduleMeta}>
                      {d.partner?.name ? `Partner: ${d.partner.name}` : "Partner: not yet assigned"}
                    </div>
                  </div>
                  <span style={{ ...S.membVisitBadge, color: meta.color, background: meta.bg }}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {membership.plan_id === "platinum-package" && Number(membership.festival_services) > 0 && (
          <FestivalCleaningPerk membership={membership} />
        )}

        <button
          style={{ width: "100%", marginTop: 14, height: 44, borderRadius: 12, border: "1px solid #d1d5db", background: "#fff", color: "#6b7280", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FESTIVAL CLEANING PERK — platinum-package only. Free pre/post-festival
// deep-cleaning visits (see PLATINUM_PACKAGE_DURATIONS `festivalServices`
// in pricing.js) redeemed as real bookings in special_service_rapidCleaning
// (see redeem_festival_cleaning() on the backend) — capped at 2 hrs, date
// picked against live seat availability (same atomic reservation "schedule
// concept" as every other membership visit slot).
// ─────────────────────────────────────────────────────────────────────────────
const FESTIVAL_SERVICE_TYPES = [
  { id: "pre-festival",  name: "Pre-Festival Cleaning"  },
  { id: "post-festival", name: "Post-Festival Cleaning" },
];

const festivalTomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const FestivalCleaningPerk = ({ membership }) => {
  const [status,     setStatus]     = useState(null); // { entitled, redeemed, remaining, redemptions }
  const [loading,    setLoading]    = useState(false);
  const [formOpen,   setFormOpen]   = useState(false);

  const [serviceType, setServiceType] = useState(FESTIVAL_SERVICE_TYPES[0].id);
  const [date,         setDate]        = useState(festivalTomorrowStr());
  const [slots,        setSlots]       = useState([]);
  const [loadingSlots,  setLoadingSlots] = useState(false);
  const [slotId,        setSlotId]       = useState("");
  const [instructions,  setInstructions] = useState("");
  const [redeeming,     setRedeeming]    = useState(false);
  const [redeemError,   setRedeemError]  = useState("");

  const loadStatus = () => {
    setLoading(true);
    apiFetch(API.festivalCleaningStatus(membership.membership_id))
      .then((data) => setStatus(data?.data || null))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStatus(); }, [membership.membership_id]); // eslint-disable-line

  useEffect(() => {
    if (!formOpen || !date) return;
    let cancelled = false;
    setLoadingSlots(true);
    apiFetch(API.membershipSlotAvailability(), {
      method: "POST",
      body: JSON.stringify({ plan_id: "platinum-festival-cleaning", start_date: date }),
    })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.data?.slots) ? data.data.slots : [];
        setSlots(list);
        setSlotId((prev) => (list.find((s) => s.slot_id === prev)?.availability ? prev : ""));
      })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [formOpen, date]);

  const handleRedeem = async () => {
    if (!slotId || redeeming) return;
    setRedeeming(true);
    setRedeemError("");
    try {
      const data = await apiFetch(API.festivalCleaningRedeem(membership.membership_id), {
        method: "POST",
        body: JSON.stringify({
          mobile: membership.mobile, service_type: serviceType, date, slot_id: slotId, instructions,
        }),
      });
      if (!data?.success) throw new Error(data?.message || "Could not redeem.");
      setFormOpen(false);
      setSlotId(""); setInstructions("");
      loadStatus();
    } catch (e) {
      setRedeemError(e.message || "Could not redeem. Please try again.");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div style={S.festPerkBox}>
      <div style={S.festPerkHead}>
        <span>🎉 Free Festival Cleaning</span>
        {!loading && status && (
          <span style={S.festPerkCount}>{status.redeemed} of {status.entitled} used</span>
        )}
      </div>

      {!loading && status?.redemptions?.length > 0 && (
        <div style={S.membScheduleList}>
          {status.redemptions.map((r) => (
            <div key={r.process_id} style={S.membScheduleRow}>
              <div>
                <div style={S.membScheduleDate}>{r.service_type?.name || "Festival Cleaning"}</div>
                <div style={S.membScheduleMeta}>{r.schedule?.date} · {r.schedule?.slot_label || r.schedule?.time_range}</div>
              </div>
              <span style={{ ...S.membVisitBadge, color: "#1d4ed8", background: "#dbeafe" }}>Booked</span>
            </div>
          ))}
        </div>
      )}

      {!loading && status && status.remaining > 0 && !formOpen && (
        <button style={S.festPerkBtn} onClick={() => setFormOpen(true)}>
          Redeem a Free Cleaning ({status.remaining} left)
        </button>
      )}

      {formOpen && (
        <div style={S.festPerkForm}>
          <div style={S.festPerkFieldLabel}>Service</div>
          <div style={S.festPerkTypeRow}>
            {FESTIVAL_SERVICE_TYPES.map((t) => (
              <button
                key={t.id} type="button"
                style={{ ...S.festPerkTypeBtn, ...(serviceType === t.id ? S.festPerkTypeBtnActive : {}) }}
                onClick={() => setServiceType(t.id)}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div style={S.festPerkFieldLabel}>Date</div>
          <input
            type="date" style={S.festPerkDateInput}
            value={date} min={festivalTomorrowStr()}
            onChange={(e) => { setDate(e.target.value); setSlotId(""); }}
          />

          <div style={S.festPerkFieldLabel}>Time Slot (2 hrs)</div>
          {loadingSlots ? (
            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>Checking availability…</p>
          ) : (
            <div style={S.festPerkSlotGrid}>
              {slots.map((s) => (
                <button
                  key={s.slot_id} type="button" disabled={!s.availability}
                  style={{ ...S.festPerkSlotBtn, ...(slotId === s.slot_id ? S.festPerkSlotBtnActive : {}), ...(!s.availability ? S.festPerkSlotBtnFull : {}) }}
                  onClick={() => s.availability && setSlotId(s.slot_id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {redeemError && <div style={{ color: "#dc2626", fontSize: 12, margin: "6px 0" }}>{redeemError}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              style={{ ...S.festPerkBtn, opacity: slotId ? 1 : 0.5, cursor: slotId ? "pointer" : "not-allowed" }}
              disabled={!slotId || redeeming}
              onClick={handleRedeem}
            >
              {redeeming ? "Booking…" : "Confirm Booking"}
            </button>
            <button
              style={{ ...S.festPerkBtn, background: "#fff", color: "#6b7280", border: "1px solid #d1d5db" }}
              onClick={() => setFormOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!loading && status && status.remaining === 0 && !status.redemptions?.length && (
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No credits on this plan.</p>
      )}
    </div>
  );
};

const BABY_STATUS = {
  0:  { label: "Pending",    color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  1:  { label: "Assigned",   color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  2:  { label: "Completed",  color: "#14532d", bg: "#dcfce7", dot: "#22c55e" },
  10: { label: "Cancelled",  color: "#6b7280", bg: "#f3f4f6", dot: "#9ca3af" },
};

const BField = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={S.bfieldBox}>
      <div style={S.bfieldLabel}>{label}</div>
      <div style={S.bfieldVal}>{value}</div>
    </div>
  );
};

const BabyCaretakerCard = ({ item, onCancel, currentMobile }) => {
  const [showProcess,   setShowProcess]   = useState(false);
  const [showComplaint, setShowComplaint] = useState(false);

  const s         = BABY_STATUS[item?.status] ?? BABY_STATUS[0];
  const langs     = Array.isArray(item?.preferred_language)
    ? item.preferred_language.join(", ")
    : item?.preferred_language || "";
  const services  = Array.isArray(item?.service_list) ? item.service_list : [];
  const worker    = item?.assigned_worker || {};
  const hasWorker = worker?.name || worker?.mobile || worker?.id;
  const canCancel = item?.status === 0;
  const isResolved= item?.status === 1 || item?.status === 2 || item?.status === 10;

  return (
    <>
    <div style={S.bcCard}>
      {/* ── Card header ── */}
      <div style={S.bcHeader}>
        <div>
          <div style={S.bcTitle}>Baby Caretaker Requirement</div>
          <div style={S.bcMeta}>
            {item?.booking_id && <>Booking ID: <strong>{item.booking_id}</strong>&nbsp;·&nbsp;</>}
            Order: <strong>{item?.order_id || "—"}</strong>
            &nbsp;·&nbsp;
            Submitted: {item?.order_datetime || item?.created_at || "—"}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", justifyContent:"flex-end" }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:s.dot, display:"inline-block" }} />
          <span style={{ ...S.bcBadge, color:s.color, background:s.bg }}>{s.label}</span>
          {canCancel && onCancel && (
            <button style={S.bcCancelBtn} onClick={() => onCancel(item.id)}>Cancel</button>
          )}
        </div>
      </div>

      {/* ── Field grid ── */}
      <div style={S.bcGrid}>
        <BField label="Name"               value={item?.name} />
        <BField label="Mobile"             value={item?.mobile_number} />
        <BField label="Email"              value={item?.email} />
        <BField label="City"               value={item?.city} />
        <BField label="Area"               value={item?.area} />
        <BField label="Address"            value={item?.address} />
        <BField label="Preferred Language" value={langs} />
        <BField label="Preferred Gender"   value={item?.preferred_gender} />
        <BField label="Priority"           value={item?.preferred_priority} />
        <BField label="Duration"           value={item?.preferred_duration} />
        <BField label="Salary Offered"     value={item?.salary_offered} />
        {item?.start_date && <BField label="Start Date" value={item.start_date} />}
        {item?.end_date   && <BField label="End Date"   value={item.end_date} />}
        {item?.time_slot  && <BField label="Time Slot"  value={item.time_slot} />}
      </div>

      {/* ── Services required ── */}
      {services.length > 0 && (
        <div style={S.bcSection}>
          <div style={S.bcSectionTitle}>Services Required</div>
          <div style={S.bcTagRow}>
            {services.map((sv, i) => <span key={i} style={S.bcTag}>{sv}</span>)}
          </div>
        </div>
      )}

      {/* ── Additional notes ── */}
      {item?.additional_note && (
        <div style={S.bcSection}>
          <div style={S.bcSectionTitle}>Additional Notes</div>
          <div style={S.bcNote}>{item.additional_note}</div>
        </div>
      )}

      {/* ── Status reason — colour-coded by status ── */}
      {item?.reason && (() => {
        const isCancelled = item.status === 10;
        return (
          <div style={{
            ...S.bcSection,
            background:   isCancelled ? "#fef2f2" : "#f0fdf4",
            border:       `1px solid ${isCancelled ? "#fecaca" : "#bbf7d0"}`,
            borderRadius: 10,
            padding:      "12px 14px",
          }}>
            <div style={{ ...S.bcSectionTitle, color: isCancelled ? "#dc2626" : "#15803d", marginBottom:4 }}>
              {isCancelled ? "Cancellation Reason" : "Status Reason"}
            </div>
            <div style={{ fontSize:14, fontWeight:600, color: isCancelled ? "#991b1b" : "#166534" }}>
              {item.reason}
            </div>
          </div>
        );
      })()}

      {/* ── Assigned worker ── */}
      {hasWorker && (
        <div style={{ ...S.bcSection, background:"#eff6ff", borderRadius:10, padding:"10px 14px" }}>
          <div style={{ ...S.bcSectionTitle, color:"#1d4ed8" }}>Assigned Worker</div>
          <div style={S.bcGrid}>
            <BField label="Name"   value={worker?.name} />
            <BField label="Mobile" value={worker?.mobile} />
            <BField label="ID"     value={worker?.id} />
          </div>
        </div>
      )}

      {/* ── Action buttons for resolved requests ── */}
      {isResolved && (
        <div style={S.bcActionRow}>
          {(item?.status === 1 || item?.status === 2) && (
            <button style={S.bcProcessBtn} onClick={() => setShowProcess(true)}>
              Process Details
            </button>
          )}
          <button style={S.bcComplaintBtn} onClick={() => setShowComplaint(true)}>
            Raise Complaint
          </button>
        </div>
      )}
    </div>

    {showProcess && (
      <ProcessDetailsModal item={item} onClose={() => setShowProcess(false)} />
    )}
    {showComplaint && (
      <RaiseComplaintModal item={item} mobile={currentMobile} onClose={() => setShowComplaint(false)} />
    )}
    </>
  );
};

// ── ProcessDetailsModal ────────────────────────────────────────────────────
const ProcessDetailsModal = ({ item, onClose }) => {
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res  = await apiFetch(API.babyCaretakerSlots(item.id));
        const data = res?.data ?? res;
        setProcess(data);
      } catch (e) { setErr(e.message || "Failed to load details."); }
      finally     { setLoading(false); }
    })();
  }, [item.id]);

  const slots    = process?.slots || [];
  const done     = slots.filter(s => s.status === 1).length;
  const total    = slots.length;
  const progress = total ? Math.round((done / total) * 100) : 0;

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={S.modalHead}>
          <div>
            <div style={{ fontWeight:800, fontSize:17, marginBottom:4 }}>Process Details</div>
            <div style={{ fontSize:12, color:"#6b7280" }}>
              {(process?.booking_id || item?.booking_id) && (
                <>Booking ID: <strong>{process?.booking_id || item?.booking_id}</strong></>
              )}
            </div>
          </div>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>

        {/* Schedule chips */}
        <div style={S.modalInfoRow}>
          {(process?.start_date || item?.start_date) && (
            <span style={S.modalInfoChip}>Start: {process?.start_date || item?.start_date}</span>
          )}
          {(process?.end_date || item?.end_date) && (
            <span style={S.modalInfoChip}>End: {process?.end_date || item?.end_date}</span>
          )}
          {(process?.time_slot || item?.time_slot) && (
            <span style={S.modalInfoChip}>Slot: {process?.time_slot || item?.time_slot}</span>
          )}
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <div style={{ margin:"12px 0" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#6b7280", marginBottom:6 }}>
              <span style={{ fontWeight:700 }}>Completion Progress</span>
              <span>{done} / {total} days completed</span>
            </div>
            <div style={{ height:10, background:"#e5e7eb", borderRadius:999, overflow:"hidden" }}>
              <div style={{ height:10, background: progress===100 ? "#22c55e" : "#3b82f6", borderRadius:999, width:`${progress}%`, transition:"width .4s" }} />
            </div>
          </div>
        )}

        {/* Slots table */}
        <div style={S.slotTableWrap}>
          {loading ? (
            <p style={{ color:"#6b7280", fontSize:13, padding:"16px 0" }}>Loading schedule…</p>
          ) : err ? (
            <p style={{ color:"#dc2626", fontSize:13 }}>{err}</p>
          ) : slots.length === 0 ? (
            <p style={{ color:"#6b7280", fontSize:13, padding:"16px 0" }}>No schedule slots assigned yet.</p>
          ) : (
            <>
              <div style={S.slotTableHead}>
                <span>Date</span>
                <span>Time Slot</span>
                <span>Status</span>
              </div>
              {slots.map((sl, i) => (
                <div key={i} style={{ ...S.slotRow, background: i % 2 === 0 ? "#fafbff" : "#fff" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{sl.date}</span>
                  <span style={{ fontSize:13, color:"#374151" }}>{sl.time_slot || "—"}</span>
                  <span style={{
                    display:"inline-block", padding:"3px 12px", borderRadius:999, fontSize:12, fontWeight:700,
                    background: sl.status === 1 ? "#dcfce7" : "#f3f4f6",
                    color:      sl.status === 1 ? "#15803d" : "#6b7280",
                  }}>
                    {sl.status === 1 ? "Completed" : "Not Completed"}
                  </span>
                  {sl.notes && <div style={{ fontSize:11, color:"#9ca3af", gridColumn:"1/-1", marginTop:2 }}>{sl.notes}</div>}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── RaiseComplaintModal ────────────────────────────────────────────────────
const COMPLAINT_SUBJECTS = [
  "Service Quality", "Worker Behaviour", "Schedule Issue",
  "Delay in Assignment", "Payment Issue", "Other",
];

const RaiseComplaintModal = ({ item, mobile, onClose }) => {
  const [form,    setForm]    = useState({ subject: "", description: "" });
  const [sending, setSending] = useState(false);
  const [done,    setDone]    = useState(false);
  const [err,     setErr]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { setErr("Please describe the issue."); return; }
    try {
      setSending(true); setErr("");
      await apiFetch(API.raiseComplaint(), {
        method: "POST",
        body:   JSON.stringify({
          booking_id:  item?.booking_id || item?.id,
          order_id:    item?.order_id,
          mobile:      mobile,
          subject:     form.subject,
          description: form.description,
        }),
      });
      setDone(true);
    } catch (e) { setErr(e.message || "Failed to raise complaint."); }
    finally     { setSending(false); }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth:480 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div style={{ fontWeight:800, fontSize:17 }}>Raise a Complaint</div>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>

        {done ? (
          <div style={{ textAlign:"center", padding:"28px 0" }}>
            <div style={{ fontSize:42, marginBottom:10 }}>✅</div>
            <div style={{ fontWeight:800, fontSize:16, color:"#15803d" }}>Complaint Raised Successfully</div>
            <div style={{ fontSize:13, color:"#6b7280", marginTop:8 }}>
              Our team will review and get back to you shortly.
            </div>
            <button style={{ ...S.proceedBtn, marginTop:20, height:40, fontSize:14 }} onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom:14 }}>
              <div style={S.bcSectionTitle}>Booking ID</div>
              <div style={{ fontSize:14, fontWeight:600, color:"#111827" }}>
                {item?.booking_id || item?.id || "—"}
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ ...S.bcSectionTitle, display:"block", marginBottom:6 }}>Subject</label>
              <select
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                style={S.cmpSelect}
              >
                <option value="">Select a subject…</option>
                {COMPLAINT_SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ ...S.bcSectionTitle, display:"block", marginBottom:6 }}>
                Description <span style={{ color:"#dc2626" }}>*</span>
              </label>
              <textarea
                rows={4}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe your issue in detail…"
                style={S.cmpTextarea}
              />
            </div>

            {err && <div style={{ color:"#dc2626", fontSize:13, marginBottom:10 }}>{err}</div>}

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button type="button" onClick={onClose} style={S.cmpCancelBtn}>Cancel</button>
              <button type="submit" disabled={sending} style={{ ...S.proceedBtn, height:40, fontSize:14, opacity: sending ? 0.7 : 1 }}>
                {sending ? "Submitting…" : "Submit Complaint"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Address constants ──────────────────────────────────────────────────────
const ADDR_LABELS  = ["Home", "Work", "Other"];
const ADDR_ICONS   = { Home: "🏠", Work: "🏢", Other: "📍" };
const EMPTY_ADDR   = { id: "", label: "Home", line1: "", area: "", city: "", state: "", pincode: "", map_url: "" };

// ── AddressModal ───────────────────────────────────────────────────────────
const AddressModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || { ...EMPTY_ADDR });
  // Whatever coordinates the live preview below is currently showing —
  // seeded from an existing saved address's lat/lng (if editing one), then
  // kept in sync by GoogleMapPreview's onResolved as the address is
  // (re-)geocoded on every text edit. Kept separate from `form.lat/lng` so
  // passing that into GoogleMapPreview never "freezes" it onto stale
  // coordinates once the address text changes (see GoogleMapPreview's
  // hasCoords contract).
  const [resolvedCoords, setResolvedCoords] = useState(
    typeof initial?.lat === "number" && typeof initial?.lng === "number"
      ? { lat: initial.lat, lng: initial.lng } : null
  );
  // True right after GPS detection or a manual pin drag/tap — these already
  // give an exact, known-good lat/lng, so the preview below is told to use
  // it directly instead of re-deriving a position by forward-geocoding the
  // (possibly abbreviated) text fields. Reset back to false the moment the
  // customer types into any address field by hand, so editing resumes the
  // normal text -> map geocoding flow.
  const [trustCoords, setTrustCoords] = useState(
    typeof initial?.lat === "number" && typeof initial?.lng === "number"
  );

  const set = (k, v) => { setTrustCoords(false); setForm(f => ({ ...f, [k]: v })); };

  // "Use My Current Location" — GPS + reverse-geocode, fills every field in
  // one tap instead of typing them out (CurrentLocationDetector.js already
  // does the whole GPS-permission + Geocoder round trip). The map below is
  // shown immediately from these exact coordinates (trustCoords), not by
  // re-geocoding the resulting text — avoids a redundant, failure-prone
  // extra round trip right after a fetch that already gave us the answer.
  const handleLocationDetected = (loc) => {
    setForm(f => ({
      ...f,
      line1: loc.line1 || f.line1, area: loc.area || f.area, city: loc.city || f.city,
      state: loc.state || f.state, pincode: loc.pincode || f.pincode,
    }));
    setResolvedCoords({ lat: loc.lat, lng: loc.lng });
    setTrustCoords(true);
  };

  // Dragging/tapping the pin on the map below — GoogleMapPreview reverse-
  // geocodes the dropped position itself and hands back both the coords and
  // (when resolvable) the matching address fields, so fine-tuning the pin
  // keeps the typed address text in sync too.
  const handlePinMoved = (coords, fields) => {
    setResolvedCoords(coords);
    setTrustCoords(true);
    if (fields) {
      setForm(f => ({
        ...f,
        line1: fields.line1 || f.line1, area: fields.area || f.area, city: fields.city || f.city,
        state: fields.state || f.state, pincode: fields.pincode || f.pincode,
      }));
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.line1.trim() || !form.city.trim()) {
      alert("Address Line 1 and City are required.");
      return;
    }
    const id = form.id || `addr-${Date.now()}`;
    // map_url kept for any other consumer expecting that field — the
    // customer-facing preview below no longer reads it, it geocodes
    // the address fields live instead.
    onSave({
      ...form, id, map_url: buildStaticMapUrl(form),
      lat: resolvedCoords?.lat ?? null,
      lng: resolvedCoords?.lng ?? null,
      geo_location: resolvedCoords ? [resolvedCoords.lat, resolvedCoords.lng] : null,
    });
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth:520 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div style={{ fontWeight:800, fontSize:17 }}>
            {initial?.id ? "Edit Address" : "Add New Address"}
          </div>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSave}>
          {/* Fastest path — GPS + reverse-geocode fills every field below
              in one tap; the map further down still lets you drag/tap to
              fine-tune the exact pin afterward. */}
          <div style={{ marginBottom: 16 }}>
            <CurrentLocationDetector
              onDetected={handleLocationDetected}
              buttonStyle={S.useLocationBtn}
              errorStyle={S.useLocationError}
            />
          </div>

          {/* Label */}
          <div style={S.addrFormRow}>
            <label style={S.addrLabel}>Label</label>
            <div style={{ display:"flex", gap:8 }}>
              {ADDR_LABELS.map(l => (
                <button key={l} type="button"
                  onClick={() => set("label", l)}
                  style={{ ...S.addrLabelChip, ...(form.label === l ? S.addrLabelActive : {}) }}>
                  {ADDR_ICONS[l]} {l}
                </button>
              ))}
            </div>
          </div>

          {/* Address fields */}
          <div style={S.addrFormRow}>
            <label style={S.addrLabel}>Street / Line 1 *</label>
            <input style={S.addrInput} value={form.line1} onChange={e => set("line1", e.target.value)}
              placeholder="House no., Street name" />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={S.addrFormRow}>
              <label style={S.addrLabel}>Area</label>
              <input style={S.addrInput} value={form.area} onChange={e => set("area", e.target.value)}
                placeholder="Locality / Area" />
            </div>
            <div style={S.addrFormRow}>
              <label style={S.addrLabel}>City *</label>
              <input style={S.addrInput} value={form.city} onChange={e => set("city", e.target.value)}
                placeholder="City" />
            </div>
            <div style={S.addrFormRow}>
              <label style={S.addrLabel}>State</label>
              <input style={S.addrInput} value={form.state} onChange={e => set("state", e.target.value)}
                placeholder="State" />
            </div>
            <div style={S.addrFormRow}>
              <label style={S.addrLabel}>Pincode</label>
              <input style={S.addrInput} value={form.pincode} onChange={e => set("pincode", e.target.value.replace(/\D/g,"").slice(0,6))}
                placeholder="6-digit pincode" maxLength={6} />
            </div>
          </div>

          {/* Live, editable map preview. Right after GPS detection or a pin
              drag (trustCoords), the exact known coordinates are passed
              straight through — GoogleMapPreview shows them immediately,
              with no re-geocoding round trip at all. Otherwise it geocodes
              the typed address fields as you type (debounced) so editing
              text still keeps the pin in sync. The pin is always
              draggable/tappable to fine-tune beyond what either source
              resolves to — dragging it updates the text fields too
              (handlePinMoved). onResolved captures whatever the preview is
              currently showing into resolvedCoords, which is what actually
              gets saved either way. */}
          {(form.line1 || form.city || resolvedCoords) && (
            <div style={{ marginBottom:14, borderRadius:12, overflow:"hidden", border:"1px solid #e5e7eb" }}>
              <GoogleMapPreview
                addr={{
                  ...(trustCoords && resolvedCoords ? { lat: resolvedCoords.lat, lng: resolvedCoords.lng } : {}),
                  line1: form.line1, area: form.area, city: form.city, state: form.state, pincode: form.pincode,
                }}
                onResolved={setResolvedCoords}
                editable
                onPinMoved={handlePinMoved}
                height={160} />
            </div>
          )}

          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button type="button" onClick={onClose} style={S.cmpCancelBtn}>Cancel</button>
            <button type="submit" style={{ ...S.proceedBtn, height:40, fontSize:14 }}>Save Address</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Opens Google Maps' own turn-by-turn directions to this address in a new
// tab — prefers the saved lat/lng (exact pin) and falls back to the typed
// address text when no coordinates were ever resolved for it.
const directionsUrlFor = (addr) => {
  const dest = typeof addr?.lat === "number" && typeof addr?.lng === "number"
    ? `${addr.lat},${addr.lng}`
    : [addr?.line1, addr?.area, addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(", ");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
};

// ── AddressCard ────────────────────────────────────────────────────────────
const AddressCard = ({ addr, onEdit, onDelete }) => (
  <div style={S.addrCard}>
    <div style={S.addrCardTop}>
      <span style={S.addrLabelBadge}>{ADDR_ICONS[addr.label] || "📍"} {addr.label}</span>
      <div style={{ display:"flex", gap:8 }}>
        <button style={S.addrEditBtn} onClick={() => onEdit(addr)}>Edit</button>
        <button style={S.addrDelBtn}  onClick={() => onDelete(addr.id)}>Delete</button>
      </div>
    </div>
    <div style={S.addrText}>
      {[addr.line1, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
    </div>
    {addr.line1 && (
      <>
        <div style={{ marginTop:10, borderRadius:10, overflow:"hidden", border:"1px solid #e5e7eb" }}>
          <GoogleMapPreview addr={addr} height={150} />
        </div>
        <a href={directionsUrlFor(addr)} target="_blank" rel="noopener noreferrer"
          style={{ ...S.addrNavBtn, marginTop:10 }}>
          🧭 Get Directions
        </a>
      </>
    )}
  </div>
);

// ── AddressSection ─────────────────────────────────────────────────────────
const AddressSection = ({ addresses, saving, onSave, onRefresh }) => {
  const [editing, setEditing] = useState(null); // null | EMPTY_ADDR | existing addr

  const handleSaveAddr = (addr) => {
    const exists = addresses.findIndex(a => a.id === addr.id);
    const next   = exists >= 0
      ? addresses.map(a => a.id === addr.id ? addr : a)
      : [...addresses, addr];
    onSave(next);
    setEditing(null);
  };

  const handleDelete = (id) => {
    onSave(addresses.filter(a => a.id !== id));
  };

  return (
    <div style={S.card}>
      <div style={S.secHead}>
        <h2 style={S.secTitle}>My Addresses</h2>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {saving && <span style={{ fontSize:12, color:"#6b7280" }}>Saving…</span>}
          <button style={S.secLink} onClick={onRefresh}>Refresh →</button>
          {addresses.length < 3 && (
            <button style={S.addrAddBtn} onClick={() => setEditing({ ...EMPTY_ADDR })}>
              + Add Address
            </button>
          )}
        </div>
      </div>

      {addresses.length === 0 ? (
        <div style={{ textAlign:"center", padding:"32px 0" }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📍</div>
          <p style={S.empty}>No addresses saved. Add up to 3 addresses.</p>
          <button style={S.addrAddBtn} onClick={() => setEditing({ ...EMPTY_ADDR })}>
            + Add Address
          </button>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:16, marginTop:8 }}>
          {addresses.map(addr => (
            <AddressCard key={addr.id} addr={addr}
              onEdit={a => setEditing(a)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {addresses.length > 0 && addresses.length < 3 && (
        <div style={{ marginTop:14, textAlign:"center" }}>
          <span style={{ fontSize:12, color:"#9ca3af" }}>{3 - addresses.length} more address slot{3 - addresses.length > 1 ? "s" : ""} available</span>
        </div>
      )}

      {editing && (
        <AddressModal initial={editing} onSave={handleSaveAddr} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const CartRow = ({ item, onQty, onRemove }) => (
  <div style={S.cartRowBox}>
    <img src={item.image || FALLBACK_IMG} alt={item.title || item.name} style={S.cartRowImg}
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }} />
    <div style={{ flex:1 }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>{item.title || item.name}</div>
      <div style={{ fontSize:13, color:"#6b7280" }}>{item.subtitle || item.short}</div>
      <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>{item.category}</div>
    </div>
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={S.qtyRow}>
        <button style={S.qtyBtn} onClick={() => onQty(item.id, (item.qty||1)-1)}>−</button>
        <span style={S.qtyNum}>{item.qty||1}</span>
        <button style={S.qtyBtn} onClick={() => onQty(item.id, (item.qty||1)+1)}>+</button>
      </div>
      <span style={{ fontWeight:800, minWidth:80, textAlign:"right" }}>
        {fmt(Number(item.price||0) * Number(item.qty||1))}
      </span>
      <button style={S.removeBtn} onClick={() => onRemove(item.id)}>Remove</button>
    </div>
  </div>
);

const SkelRows = () => (
  <div>{[1,2,3].map((n) => (
    <div key={n} style={{ display:"flex", justifyContent:"space-between", padding:"14px 0", borderBottom:"1px solid #f1f3f8" }}>
      <div style={{ width:200, height:14, borderRadius:999, background:"#e5e7eb" }} />
      <div style={{ width:80,  height:14, borderRadius:999, background:"#e5e7eb" }} />
    </div>
  ))}</div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:          { minHeight:"100vh", background:"#f6f8fc", fontFamily:"Arial,sans-serif", color:"#111827" },
  toast:         { position:"fixed", top:20, right:20, background:"#111827", color:"#fff", padding:"12px 16px", borderRadius:12, zIndex:9999, boxShadow:"0 10px 30px rgba(0,0,0,.15)", fontWeight:600 },
  loadBox:       { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", gap:12, fontSize:18, fontWeight:700 },
  spinner:       { width:22, height:22, border:"3px solid #d1d5db", borderTop:"3px solid #4f46e5", borderRadius:"50%", animation:"spin 1s linear infinite" },
  layout:        { display:"grid", gridTemplateColumns:"280px 1fr", minHeight:"100vh" },
  sidebar:       { background:"#fff", borderRight:"1px solid #e5e7eb", padding:"22px 16px" },
  profileMini:   { display:"flex", alignItems:"center", gap:12, padding:"14px", background:"#f8f9ff", borderRadius:16, marginBottom:20 },
  miniAvatar:    { width:46, height:46, borderRadius:"50%", objectFit:"cover", border:"2px solid #e5e7eb" },
  miniName:      { fontWeight:800, fontSize:15, marginBottom:4 },
  miniMeta:      { fontSize:12, color:"#6b7280" },
  navGroup:      { display:"flex", flexDirection:"column", gap:8, marginBottom:16 },
  navItem:       { width:"100%", textAlign:"left", border:"none", background:"transparent", height:44, borderRadius:12, padding:"0 14px", cursor:"pointer", fontWeight:700, color:"#374151", display:"flex", justifyContent:"space-between", alignItems:"center" },
  navActive:     { background:"#eef2ff", color:"#4338ca" },
  navBadge:      { background:"#4f46e5", color:"#fff", borderRadius:999, padding:"2px 8px", fontSize:12, fontWeight:800 },
  logoutBtn:     { width:"100%", height:46, border:"none", borderRadius:14, background:"#111827", color:"#fff", fontWeight:700, cursor:"pointer", marginTop:14 },

  // ── Sidebar coupon widget ──────────────────────────────────────────────
  sidebarCouponBox:   { background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:16, padding:14, marginBottom:16, display:"flex", flexDirection:"column", gap:8 },
  sidebarCouponTitle: { fontSize:13, fontWeight:800, color:"#9a3412", marginBottom:2 },
  sidebarCouponCard:  { display:"flex", justifyContent:"space-between", alignItems:"center", background:"#fff", border:"1px dashed #fdba74", borderRadius:10, padding:"8px 10px" },
  sidebarCouponCode:  { fontSize:13, fontWeight:800, color:"#111827", letterSpacing:"0.02em" },
  sidebarCouponPct:   { fontSize:12, fontWeight:800, color:"#ea580c" },
  main:          { padding:24 },
  errBox:        { marginBottom:16, background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", padding:"12px 14px", borderRadius:12, fontWeight:600 },
  heroRow:       { display:"grid", gridTemplateColumns:"1fr", gap:20, marginBottom:20 },
  heroCard:      { background:"#fff", border:"1px solid #eceef6", borderRadius:24, padding:24, display:"flex", justifyContent:"space-between", alignItems:"center", gap:20 },
  heroLeft:      { display:"flex", alignItems:"center", gap:18 },
  heroAvatar:    { width:110, height:110, borderRadius:24, objectFit:"cover" },
  avatarSkel:    { width:110, height:110, borderRadius:24, background:"#e5e7eb" },
  heroName:      { margin:"0 0 8px", fontSize:28, fontWeight:800 },
  verifiedBadge: { display:"inline-flex", background:"#e8f9ee", color:"#15803d", borderRadius:999, padding:"6px 12px", fontSize:12, fontWeight:800, marginBottom:10 },
  heroBio:       { fontSize:14, color:"#6b7280", marginBottom:6 },
  heroStats:     { display:"flex", gap:20 },
  statLabel:     { fontSize:12, color:"#9ca3af", marginBottom:6, fontWeight:700 },
  statVal:       { fontSize:22, fontWeight:800 },
  secHead:       { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 },
  secTitle:      { margin:0, fontSize:22, fontWeight:800 },
  secLink:       { border:"none", background:"transparent", color:"#4f46e5", fontWeight:700, cursor:"pointer" },
  bookedTabRow:  { display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" },
  bookedTabBtn:  { padding:"8px 18px", borderRadius:999, border:"1px solid #e5e7eb", background:"#f3f4f6", color:"#6b7280", fontWeight:600, fontSize:14, cursor:"pointer" },
  bookedTabActive: { background:"#4f46e5", color:"#fff", border:"1px solid #4f46e5" },
  card:          { background:"#fff", border:"1px solid #eceef6", borderRadius:20, padding:20 },
  listCard:      { background:"#fff", border:"1px solid #eceef6", borderRadius:20, padding:12 },
  empty:         { color:"#6b7280", fontSize:14, padding:"10px 0" },
  bookRow:       { display:"grid", gridTemplateColumns:"90px 1fr 120px", gap:14, alignItems:"center", padding:"12px 0", borderBottom:"1px solid #f1f3f8" },
  bookImg:       { width:90, height:70, objectFit:"cover", borderRadius:12 },
  statusBadge:   { display:"inline-block", padding:"4px 10px", borderRadius:999, background:"#e8f9ee", color:"#15803d", fontSize:12, fontWeight:700, marginBottom:8 },

  // ── Main "My Coupons" card ───────────────────────────────────────────────
  couponGrid:        { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:14 },
  couponCard:        { border:"1.5px dashed #fdba74", background:"#fffaf3", borderRadius:14, padding:14 },
  couponCardTop:     { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, gap:8 },
  couponBadge:       { background:"#ea580c", color:"#fff", borderRadius:999, padding:"3px 10px", fontSize:12, fontWeight:800, whiteSpace:"nowrap" },
  couponExpiry:      { fontSize:11, color:"#9ca3af", fontWeight:600, textAlign:"right" },
  couponCode:        { fontSize:17, fontWeight:900, color:"#111827", letterSpacing:"0.03em" },
  couponReferralNote:{ marginTop:14, paddingTop:12, borderTop:"1px solid #f1f3f8", fontSize:12.5, color:"#6b7280", fontWeight:600 },
  addBtn:        { width:"100%", height:36, border:"none", borderRadius:10, background:"#4f46e5", color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13 },
  cartRowBox:    { display:"grid", gridTemplateColumns:"90px 1fr auto", gap:16, alignItems:"center", padding:"14px 0", borderBottom:"1px solid #f1f3f8" },
  cartRowImg:    { width:90, height:70, objectFit:"cover", borderRadius:12 },
  qtyRow:        { display:"flex", alignItems:"center", gap:8 },
  qtyBtn:        { width:30, height:30, border:"1px solid #e5e7eb", borderRadius:8, background:"#f9fafb", fontWeight:800, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" },
  qtySmBtn:      { width:26, height:26, border:"1px solid #e5e7eb", borderRadius:6, background:"#f9fafb", fontWeight:800, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" },
  qtySmBtnP:     { width:26, height:26, border:"none", borderRadius:6, background:"#4f46e5", color:"#fff", fontWeight:800, cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" },
  qtyNum:        { fontSize:15, fontWeight:700, minWidth:20, textAlign:"center" },
  removeBtn:     { border:"none", background:"transparent", color:"#ef4444", fontWeight:700, cursor:"pointer" },
  clearBtn:      { border:"none", background:"#fef2f2", color:"#dc2626", height:40, padding:"0 14px", borderRadius:12, fontWeight:700, cursor:"pointer" },
  cartBill:      { marginTop:16, paddingTop:16, borderTop:"1px solid #e5e7eb", display:"grid", gap:10 },
  billRow:       { display:"flex", justifyContent:"space-between", fontSize:14, color:"#4b5563" },
  billTotal:     { display:"flex", justifyContent:"space-between", fontSize:18, fontWeight:800, color:"#111827", paddingTop:10, borderTop:"1px solid #e5e7eb", marginTop:4 },
  cartFoot:      { marginTop:16, display:"flex", justifyContent:"flex-end" },
  proceedBtn:    { height:46, padding:"0 24px", border:"none", borderRadius:14, background:"#4f46e5", color:"#fff", fontWeight:700, cursor:"pointer", fontSize:15 },
  dataRow:       { display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, padding:"14px 0", borderBottom:"1px solid #f1f3f8" },
  dataTitle:     { fontSize:15, fontWeight:700, marginBottom:6 },
  dataMeta:      { fontSize:13, color:"#6b7280" },
  delBtn:        { border:"none", background:"#fef2f2", color:"#dc2626", height:36, padding:"0 10px", borderRadius:10, fontWeight:700, cursor:"pointer" },

  // ── Baby Caretaker card ──────────────────────────────────────────────
  bcCard:        { border:"1px solid #e5e7eb", borderRadius:16, padding:18, marginBottom:16, background:"#fafbff" },
  bcHeader:      { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14, paddingBottom:12, borderBottom:"1px solid #f1f3f8" },
  bcTitle:       { fontSize:16, fontWeight:800, marginBottom:4, color:"#111827" },
  bcMeta:        { fontSize:12, color:"#6b7280" },
  bcBadge:       { display:"inline-block", padding:"4px 12px", borderRadius:999, fontSize:12, fontWeight:700 },
  bcGrid:        { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:"8px 16px", marginBottom:12 },
  bfieldBox:     { display:"flex", flexDirection:"column", gap:2 },
  bfieldLabel:   { fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.05em" },
  bfieldVal:     { fontSize:14, fontWeight:600, color:"#111827" },
  bcSection:     { marginBottom:10 },
  bcSectionTitle:{ fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 },
  bcTagRow:      { display:"flex", flexWrap:"wrap", gap:6 },
  bcTag:         { background:"#eef2ff", color:"#4338ca", borderRadius:999, padding:"4px 10px", fontSize:12, fontWeight:600 },
  bcNote:        { fontSize:13, color:"#374151", lineHeight:1.6, background:"#f9fafb", padding:"8px 12px", borderRadius:8 },
  bcCancelBtn:    { border:"1px solid #fca5a5", background:"#fef2f2", color:"#dc2626", borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:700, cursor:"pointer" },
  bcActionRow:    { display:"flex", gap:10, marginTop:14, paddingTop:12, borderTop:"1px solid #f1f3f8" },
  bcProcessBtn:   { border:"1px solid #c7d2fe", background:"#eef2ff", color:"#4338ca", borderRadius:10, padding:"7px 16px", fontSize:13, fontWeight:700, cursor:"pointer" },
  bcComplaintBtn: { border:"1px solid #fca5a5", background:"#fff0f0", color:"#dc2626", borderRadius:10, padding:"7px 16px", fontSize:13, fontWeight:700, cursor:"pointer" },

  // ── Modals ───────────────────────────────────────────────────────────────
  modalOverlay:   { position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 },
  modalBox:       { background:"#fff", borderRadius:20, padding:24, width:"100%", maxWidth:640, maxHeight:"85vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,.25)" },
  modalHead:      { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, paddingBottom:12, borderBottom:"1px solid #f1f3f8" },
  modalClose:     { border:"none", background:"#f3f4f6", borderRadius:8, width:32, height:32, cursor:"pointer", fontWeight:800, fontSize:15, flexShrink:0 },
  modalInfoRow:   { display:"flex", flexWrap:"wrap", gap:8, marginBottom:14 },
  modalInfoChip:  { background:"#f0f4ff", color:"#4338ca", padding:"4px 12px", borderRadius:999, fontSize:12, fontWeight:600 },

  // ── Slot table ───────────────────────────────────────────────────────────
  slotTableWrap:  { marginTop:8 },
  slotTableHead:  { display:"grid", gridTemplateColumns:"1fr 1fr 140px", gap:8, padding:"8px 12px", background:"#f8fafc", borderRadius:8, fontSize:12, fontWeight:700, color:"#6b7280", marginBottom:4 },
  slotRow:        { display:"grid", gridTemplateColumns:"1fr 1fr 140px", gap:8, padding:"9px 12px", borderRadius:8, alignItems:"center" },

  // ── Complaint form ───────────────────────────────────────────────────────
  cmpSelect:      { width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", fontSize:14, background:"#fff", outline:"none" },
  cmpTextarea:    { width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", fontSize:14, resize:"vertical", outline:"none", boxSizing:"border-box", fontFamily:"inherit" },
  cmpCancelBtn:   { height:40, padding:"0 18px", border:"1px solid #e5e7eb", borderRadius:10, background:"#fff", fontWeight:700, cursor:"pointer", fontSize:14 },

  // ── My Addresses ──────────────────────────────────────────────────────────
  addrCard:       { background:"#fff", borderRadius:16, border:"1px solid #e5e7eb", padding:"16px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", display:"flex", flexDirection:"column", gap:6 },
  addrCardTop:    { display:"flex", justifyContent:"space-between", alignItems:"center" },
  addrLabelBadge: { fontSize:13, fontWeight:700, background:"#f0f9ff", color:"#0369a1", padding:"4px 10px", borderRadius:20, border:"1px solid #bae6fd" },
  addrText:       { fontSize:14, color:"#374151", lineHeight:1.5 },
  addrEditBtn:    { fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:8, border:"1px solid #6366f1", color:"#6366f1", background:"#fff", cursor:"pointer" },
  addrDelBtn:     { fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:8, border:"1px solid #f87171", color:"#ef4444", background:"#fff", cursor:"pointer" },
  addrAddBtn:     { fontSize:13, fontWeight:700, padding:"8px 18px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", cursor:"pointer" },
  addrNavBtn:     { fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:8, border:"1px solid #16a34a", color:"#16a34a", background:"#fff", cursor:"pointer", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:4 },
  useLocationBtn: { width:"100%", fontSize:13, fontWeight:700, padding:"11px 16px", borderRadius:10, border:"1.5px solid #6366f1", color:"#6366f1", background:"#f5f3ff", cursor:"pointer" },
  useLocationError: { fontSize:12, color:"#ef4444", marginTop:6 },
  addrFormRow:    { marginBottom:12 },
  addrLabel:      { display:"block", fontSize:12, fontWeight:600, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" },
  addrInput:      { width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit" },
  addrLabelChip:  { padding:"6px 14px", borderRadius:20, border:"1px solid #e5e7eb", background:"#f9fafb", color:"#374151", fontSize:13, fontWeight:600, cursor:"pointer" },
  addrLabelActive:{ border:"2px solid #6366f1", background:"#eef2ff", color:"#6366f1" },
  addrGenBtn:     { fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:8, border:"1px solid #6366f1", color:"#6366f1", background:"#fff", cursor:"pointer" },

  // ── Order Card ───────────────────────────────────────────────────────────
  // ── Order Card ────────────────────────────────────────────────────────────
  ocCard:          { background:"#fff", borderRadius:20, padding:18, marginBottom:14, border:"1px solid #e5e7eb", boxShadow:"0 2px 8px rgba(0,0,0,0.07)" },
  ocTopRow:        { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:12 },
  ocTitle:         { fontSize:16, fontWeight:700, color:"#111827" },
  ocOrderId:       { fontSize:12, color:"#9ca3af", marginTop:2 },
  ocCatChip:       { background:"#fef9c3", color:"#713f12", fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:999, border:"1px solid #fde68a", textTransform:"capitalize" },
  ocBookingChip:   { background:"#eff6ff", color:"#1e40af", fontSize:11, fontWeight:600, padding:"4px 10px", borderRadius:999, border:"1px solid #bfdbfe" },
  ocRightTop:      { display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 },
  ocBadge:         { display:"inline-block", padding:"4px 10px", borderRadius:999, fontSize:12, fontWeight:700 },
  ocAmount:        { fontSize:18, fontWeight:800, color:"#111827" },
  // ── Order progress: 4 cards, current step bright/highlighted ──────────────
  ocStepsGrid:          { display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:7, marginTop:4, marginBottom:14 },
  ocStepCard:           { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"10px 4px", borderRadius:12, border:"1px solid #e5e7eb", background:"#f9fafb", transition:"all 0.25s ease" },
  ocStepCardDone:       { background:"#eff6ff", border:"1px solid #bfdbfe" },
  ocStepCardCurrent:    { background:"linear-gradient(135deg,#3b82f6,#1d4ed8)", border:"1px solid #1d4ed8", boxShadow:"0 6px 18px rgba(29,78,216,0.35)", transform:"scale(1.06)" },
  ocStepIconWrap:       { width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background:"#fff", border:"1px solid #e5e7eb", marginBottom:5 },
  ocStepIconWrapDone:   { background:"#dbeafe", border:"1px solid #93c5fd" },
  ocStepIconWrapCurrent:{ width:38, height:38, background:"rgba(255,255,255,0.22)", border:"1px solid rgba(255,255,255,0.55)" },
  ocStepCardLabel:      { fontSize:9.5, color:"#9ca3af", fontWeight:600, textAlign:"center", lineHeight:1.2 },
  ocStepCardLabelDone:  { color:"#1d4ed8" },
  ocStepCardLabelCurrent:{ color:"#fff", fontWeight:800, fontSize:10.5 },
  ocPartnerCard:   { display:"flex", alignItems:"center", padding:"10px 12px", borderRadius:14, background:"#f9fafb", marginBottom:10, gap:10 },
  ocPartnerAvatar: { width:40, height:40, borderRadius:"50%", background:"#4f46e5", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  ocPartnerBadge:  { padding:"4px 10px", borderRadius:999, background:"#dcfce7", fontSize:11, fontWeight:700, color:"#15803d", flexShrink:0, whiteSpace:"nowrap" },
  ocDetailsBox:    { border:"1px solid #e5e7eb", borderRadius:12, padding:"8px 12px", background:"#f9fafb", marginBottom:10 },
  ocDetailsLabel:  { fontSize:11, color:"#6b7280", fontWeight:600 },
  ocDetailsValue:  { fontSize:13, color:"#111827", fontWeight:600, marginTop:2 },
  ocExpandBtn:     { border:"none", background:"none", color:"#4f46e5", fontWeight:700, cursor:"pointer", fontSize:12, padding:0 },
  ocServiceList:   { marginTop:6 },
  ocServiceRow:    { display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #f1f3f8", fontSize:13 },
  ocActions:       { marginTop:12 },
  ocCancelBtn:     { width:"100%", padding:"10px 0", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:"#fee2e2", color:"#b91c1c", border:"1px solid #fecaca" },
  ocPayBtn:        { width:"100%", padding:"10px 0", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:"#0369a1", color:"#fff", border:"none" },
  ocCancelledNote: { marginTop:8, fontSize:12, color:"#6b7280", background:"#f3f4f6", padding:"6px 10px", borderRadius:8 },
  ocCancelInfoBox:       { marginTop:10, borderRadius:10, padding:"8px 10px", background:"#f3f4f6" },
  ocRescheduleBtn:       { marginTop:8, width:"100%", padding:"10px 0", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:"#1d4ed8", color:"#fff", border:"none" },
  ocPaidPill:            { marginTop:8, fontSize:11, color:"#0369a1", fontWeight:700, background:"#e0f2fe", padding:"5px 10px", borderRadius:8 },
  ocWorkDoneBanner:      { fontSize:13, color:"#5b21b6", background:"#ede9fe", borderRadius:8, padding:"8px 10px", marginBottom:8 },
  ocPaymentDueBox:       { marginTop:12, borderRadius:10, background:"#fef3c7", border:"1px solid #fcd34d", padding:"10px 12px" },
  ocPaymentDueText:      { fontSize:13, fontWeight:700, color:"#92400e", marginBottom:8 },
  ocPaymentReceivedBox:  { marginTop:12, borderRadius:10, background:"#e0f2fe", border:"1px solid #7dd3fc", padding:"10px 12px", fontSize:13, color:"#0369a1", fontWeight:600 },
  ocJobClosedBox:        { marginTop:12, borderRadius:10, background:"#dcfce7", border:"1px solid #86efac", padding:"10px 12px", fontSize:13, color:"#14532d", fontWeight:600 },

  // ── Cancel Modal ─────────────────────────────────────────────────────────
  modalTitle:    { margin:"0 0 4px", fontSize:20, fontWeight:800 },
  modalSub:      { margin:"0 0 14px", color:"#6b7280", fontSize:14 },
  modalRow:      { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0" },
  modalLabel:    { fontSize:14, color:"#6b7280" },
  modalVal:      { fontSize:14, fontWeight:600, color:"#111827" },
  modalBtnRow:   { display:"flex", gap:10, marginTop:20 },
  modalKeepBtn:  { flex:1, height:44, border:"1px solid #e5e7eb", borderRadius:12, background:"#fff", fontWeight:700, cursor:"pointer", fontSize:14 },
  modalConfirmBtn:{ flex:1, height:44, border:"none", borderRadius:12, background:"#dc2626", color:"#fff", fontWeight:700, cursor:"pointer", fontSize:14 },

  // ── Membership card + redeem modal ─────────────────────────────────────────
  membGrid:      { display:"grid", gridTemplateColumns:"repeat(2, minmax(0, 1fr))", gap:16 },
  membCard:      { border:"1px solid #e5e7eb", borderRadius:16, padding:18, background:"#fff", display:"flex", flexDirection:"column" },
  membTop:       { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, marginBottom:14 },
  membName:      { fontSize:17, fontWeight:800, color:"#111827" },
  membSub:       { fontSize:12, color:"#9ca3af", marginTop:3 },
  membBadge:     { fontSize:11, fontWeight:800, padding:"5px 12px", borderRadius:999, whiteSpace:"nowrap" },
  membStatsRow:  { display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10, background:"#f8fafc", borderRadius:12, padding:"14px 10px", marginBottom:14 },
  membStat:      { textAlign:"center" },
  membStatVal:   { fontSize:18, fontWeight:800, color:"#111827" },
  membStatLabel: { fontSize:11, color:"#6b7280", marginTop:2 },
  membDetailBtn: { width:"100%", height:46, border:"none", borderRadius:12, background:"#4f46e5", color:"#fff", fontWeight:800, fontSize:14, cursor:"pointer" },
  membBtnRow:    { display:"flex", gap:10 },
  membCancelBtn: { height:46, padding:"0 18px", border:"1px solid #fecaca", borderRadius:12, background:"#fff", color:"#dc2626", fontWeight:800, fontSize:14, cursor:"pointer" },
  membHistoryToggle: { width:"100%", marginTop:10, height:38, border:"1px solid #e5e7eb", borderRadius:10, background:"#fff", color:"#4f46e5", fontWeight:700, fontSize:12, cursor:"pointer" },
  membHistoryList:   { display:"flex", flexDirection:"column", gap:8, marginTop:10 },
  membVisitRow:      { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, background:"#f8fafc", borderRadius:10, padding:"10px 12px" },
  membVisitLeft:     { display:"flex", flexDirection:"column", gap:2, minWidth:0 },
  membVisitService:  { fontSize:13, fontWeight:700, color:"#111827" },
  membVisitMeta:      { fontSize:11, color:"#6b7280" },
  membVisitId:       { fontSize:10, color:"#9ca3af", marginTop:2 },
  membVisitBadge:    { fontSize:10, fontWeight:800, padding:"4px 10px", borderRadius:999, whiteSpace:"nowrap", flexShrink:0 },

  membScheduleList:  { display:"flex", flexDirection:"column", gap:8 },
  membScheduleRow:   { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, background:"#f8fafc", borderRadius:10, padding:"10px 12px" },
  membScheduleDate:  { fontSize:13, fontWeight:700, color:"#111827" },
  membScheduleMeta:  { fontSize:11, color:"#6b7280", marginTop:2 },
  membScheduleHead:  { fontSize:14, fontWeight:800, color:"#111827", margin:"16px 0 10px" },

  membInfoGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, background:"#f8fafc", borderRadius:12, padding:12, marginBottom:6 },
  membInfoItem:  { minWidth:0 },
  membInfoLabel: { fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:0.4 },
  membInfoVal:   { fontSize:13, fontWeight:600, color:"#111827", marginTop:2, wordBreak:"break-word" },

  festPerkBox:   { background:"#fdf4ff", border:"1px solid #f0abfc", borderRadius:12, padding:14, marginTop:16 },
  festPerkHead:  { display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14, fontWeight:800, color:"#111827", marginBottom:10 },
  festPerkCount: { fontSize:11, fontWeight:700, color:"#a21caf" },
  festPerkBtn:   { width:"100%", height:42, border:"none", borderRadius:10, background:"#a21caf", color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer", marginTop:10 },
  festPerkForm:  { marginTop:12, background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:12 },
  festPerkFieldLabel: { fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.3, margin:"10px 0 6px" },
  festPerkTypeRow: { display:"flex", gap:8 },
  festPerkTypeBtn: { flex:1, height:38, border:"1.5px solid #e5e7eb", borderRadius:8, background:"#fff", color:"#374151", fontWeight:700, fontSize:12, cursor:"pointer" },
  festPerkTypeBtnActive: { border:"1.5px solid #a21caf", background:"#fdf4ff", color:"#a21caf" },
  festPerkDateInput: { width:"100%", height:38, borderRadius:8, border:"1px solid #d6deeb", padding:"0 10px", fontSize:13, boxSizing:"border-box" },
  festPerkSlotGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:8 },
  festPerkSlotBtn: { border:"1.5px solid #e5e7eb", borderRadius:8, background:"#fff", color:"#374151", fontWeight:700, fontSize:11.5, padding:"8px 6px", cursor:"pointer" },
  festPerkSlotBtnActive: { border:"1.5px solid #a21caf", background:"#fdf4ff", color:"#a21caf" },
  festPerkSlotBtnFull: { background:"#f9fafb", color:"#9ca3af", cursor:"not-allowed", opacity:0.7 },
};

if (typeof document !== "undefined" && !document.getElementById("pd-anim")) {
  const s = document.createElement("style");
  s.id = "pd-anim";
  s.textContent = "@keyframes spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}

export default ProfileDashboard;